import { useEffect, useState, useRef } from "react";
import { Routes, Route, Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import DashShell, { StatCard } from "@/components/DashShell";
import { useAdminLang } from "@/auth/AdminLangCtx";
import { api, fmtMoney, formatError, CURRENCIES, COUNTRIES, LANGUAGES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  LayoutDashboard, Building2, CreditCard, Globe, Plus, Trash2, ArrowUpRight, Eye, Sparkles, Users2,
  Bell, Wallet as WalletIcon, Send as SendIcon, Settings as SettingsIcon, Link as LinkIcon, ArrowUp,
  MessageCircle, FileText, Briefcase, HandCoins, Search, Check, X, Copy, CheckCircle2,
  Paperclip, Image as ImageIcon, Mic, Download, File as FileIcon, PlusCircle, Pencil, TrendingUp,
  Gift, ExternalLink, Users, LifeBuoy, KeyRound, History, ArrowDownToLine, Repeat, Lock,
} from "lucide-react";

const BANKS_DOMAIN = "edbks.com";
const ALLOWED_BANK_DOMAINS = ["edbks.com", "ixt.finance", "ixt.app"];
const bankPublicUrl = (slug, suffix = "", domain = BANKS_DOMAIN) => `https://${slug}.${domain}${suffix}`;

const NAV = (counts, t) => [
  { key: "overview", label: t("nav.overview"), to: "/admin", icon: LayoutDashboard },
  { key: "banks", label: t("nav.banks"), to: "/admin/banks", icon: Building2, badge: counts.unread_messages, match: "/admin/banks" },
  { key: "subs", label: t("nav.subscriptions"), to: "/admin/subscriptions", icon: CreditCard, badge: counts.pending_subs, match: "/admin/subscriptions" },
  { key: "domains", label: t("nav.domains"), to: "/admin/domains", icon: Globe, match: "/admin/domains" },
  { key: "wallet", label: t("nav.wallet"), to: "/admin/wallet", icon: WalletIcon, match: "/admin/wallet" },
  { key: "referral", label: t("nav.referral"), to: "/admin/referral", icon: Gift, match: "/admin/referral" },
  { key: "support", label: t("nav.support"), to: "/admin/support", icon: LifeBuoy, match: "/admin/support" },
];

function ConfirmDialog({ open, title = "Confirmation", message, onOk, onCancel, danger = true }) {
  const { t } = useAdminLang();
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-600 py-1 leading-relaxed">{message}</p>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>{t("common.cancel")}</Button>
          <Button className={danger ? "bg-red-600 text-white hover:bg-red-700" : "bg-zinc-900 text-white"} onClick={onOk}>{t("common.confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InputDialog({ open, title, label, defaultValue = "", placeholder = "", onOk, onCancel }) {
  const { t } = useAdminLang();
  const [val, setVal] = useState(defaultValue);
  useEffect(() => { if (open) setVal(defaultValue); }, [open, defaultValue]);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="py-2">
          <Label>{label}</Label>
          <Input autoFocus value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder} onKeyDown={(e) => e.key === "Enter" && val && onOk(val)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>{t("common.cancel")}</Button>
          <Button className="bg-zinc-900 text-white" onClick={() => val && onOk(val)} disabled={!val}>{t("common.confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminApp() {
  const [stats, setStats] = useState({});
  const { t } = useAdminLang();
  const reload = () => api.get("/stats/admin").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => {
    reload();
    // Poll so the unread-messages badge stays fresh while the admin navigates.
    const intvl = setInterval(reload, 10000);
    return () => clearInterval(intvl);
  }, []);
  const counts = { pending_subs: stats.pending_subs || 0, unread_messages: stats.unread_messages || 0 };

  return (
    <DashShell role="admin" nav={NAV(counts, t)}>
      <Routes>
        <Route index element={<AdminHome stats={stats} reload={reload} />} />
        <Route path="banks" element={<MyBanks reload={reload} />} />
        <Route path="banks/new" element={<NewBank reload={reload} />} />
        <Route path="banks/:id" element={<ManageBank reload={reload} />} />
        <Route path="subscriptions" element={<MySubscriptions reload={reload} />} />
        <Route path="domains" element={<MyDomains reload={reload} />} />
        <Route path="wallet" element={<AdminWallet />} />
        <Route path="referral" element={<Referral />} />
        <Route path="support" element={<ContactSupport />} />
      </Routes>
    </DashShell>
  );
}

function AdminHome({ stats, reload }) {
  const { t } = useAdminLang();
  const [userCurrency, setUserCurrency] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const geo = await api.get("/public/geo");
        const curr = geo.data?.currency;
        if (!curr || curr === "XOF" || curr === "XAF") return;
        const r = await api.get(`/public/coin-rate?currency=${curr}`);
        if (r.data?.rate) setUserCurrency({ currency: curr, rate: r.data.rate });
      } catch {}
    })();
  }, []);

  const conversionLine = (() => {
    const parts = ["1 Coin = 1 FCFA"];
    if (userCurrency?.rate) {
      const v = userCurrency.rate;
      const formatted = v < 0.01 ? v.toFixed(5) : v < 1 ? v.toFixed(4) : v.toFixed(2);
      parts.push(`${formatted} ${userCurrency.currency}`);
    }
    return parts.join(" ≈ ");
  })();

  return (
    <div data-testid="admin-home">
      {/* Wallet block — primary action, always at top */}
      <div className="rounded-2xl p-5 sm:p-6 mb-6 flex flex-wrap items-center justify-between gap-4 text-white"
        style={{ background: "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)", boxShadow: "0 16px 40px -16px rgba(59,130,246,.55)" }}
        data-testid="ahome-wallet-block">
        <div>
          <div className="text-xs tracking-widest uppercase opacity-70 mb-1">{t("dashboard.availableBalance")}</div>
          <div className="text-4xl font-extrabold tracking-tight font-mono-num" data-testid="ahome-balance"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {fmtMoney(stats.admin_balance ?? 0, "COINS")}
          </div>
          <div className="text-xs opacity-60 mt-1">{conversionLine}</div>
        </div>
        <Link to="/admin/wallet">
          <Button className="font-bold rounded-full px-5"
            style={{ background: "#fff", color: "#3B82F6", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,.15)" }}>
            <WalletIcon className="w-4 h-4 mr-1.5" /> {t("dashboard.topUpAccount")}
          </Button>
        </Link>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <Link to="/admin/subscriptions" className="bg-white border border-zinc-200 hover:border-zinc-900 transition rounded-xl p-6" data-testid="cta-new-sub">
          <Sparkles className="w-6 h-6 text-[#D4AF37] mb-3" />
          <div className="font-display text-xl font-bold">{t("dashboard.buySubscription")}</div>
          <p className="text-zinc-500 text-sm mt-2">{t("dashboard.newSubscription")}</p>
        </Link>
        <Link to="/admin/banks/new" className="bg-white border border-zinc-200 hover:border-zinc-900 transition rounded-xl p-6" data-testid="cta-new-bank">
          <Building2 className="w-6 h-6 text-[#D4AF37] mb-3" />
          <div className="font-display text-xl font-bold">{t("dashboard.createBank")}</div>
          <p className="text-zinc-500 text-sm mt-2">{t("dashboard.chooseTemplate")}</p>
        </Link>
      </div>
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">{t("dashboard.welcome")}</div>
        <h1 className="font-display text-4xl font-extrabold tracking-tighter">{t("dashboard.title")}</h1>
        <p className="text-zinc-500 mt-1">{t("dashboard.subtitle")}</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard testId="ahome-banks" label={t("dashboard.myBanks")} value={stats.banks ?? 0} />
        <StatCard testId="ahome-active-subs" label={t("dashboard.activeSubscriptions")} value={stats.active_subs ?? 0} />
        <StatCard testId="ahome-pending-subs" label={t("dashboard.pendingSubscriptions")} value={stats.pending_subs ?? 0} color="gold" />
        <StatCard testId="ahome-bank-users" label={t("dashboard.bankUsers")} value={stats.bank_users ?? 0} />
        <StatCard testId="ahome-cards" label={t("dashboard.cardsIssued")} value={stats.cards ?? 0} />
        <StatCard testId="ahome-txns" label={t("dashboard.transactions")} value={stats.transactions ?? 0} />
      </div>
    </div>
  );
}

function MyBanks() {
  const { t } = useAdminLang();
  const [banks, setBanks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoTemplate, setDemoTemplate] = useState("tpl-classic-financial");

  useEffect(() => {
    const load = () => api.get("/banks").then((r) => setBanks(r.data || [])).catch(() => {});
    load();
    const intvl = setInterval(load, 10000);
    return () => clearInterval(intvl);
  }, []);

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const openDemo = () => {
    window.open(`https://icz.finance?template=${demoTemplate}`, "_blank", "noopener");
    setDemoOpen(false);
  };

  return (
    <div data-testid="admin-banks">
      <div className="mb-6 flex flex-wrap justify-between items-end gap-3">
        <div className="min-w-0"><h1 className="font-display text-3xl font-extrabold tracking-tighter">{t("banks.title")}</h1></div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Button variant="outline" onClick={() => setDemoOpen(true)} data-testid="banks-demo" className="flex-1 sm:flex-initial">
            <Eye className="w-4 h-4 mr-1" /> {t("banks.viewDemo")}
          </Button>
          <Link to="/admin/banks/new" className="flex-1 sm:flex-initial"><Button className="btn-pill bg-zinc-900 hover:bg-zinc-700 text-white w-full sm:w-auto" data-testid="banks-new"><Plus className="w-4 h-4 mr-1" /> {t("banks.newBank")}</Button></Link>
        </div>
      </div>

      {/* Demo template picker */}
      <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t("banks.demoTitle")}</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500">{t("banks.demoSubtitle")}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 py-2 max-h-80 overflow-y-auto">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setDemoTemplate(t.id)}
                className={`text-left rounded-xl p-4 border-2 transition ${demoTemplate === t.id ? "border-zinc-900 ring-2 ring-zinc-900/20" : "border-zinc-200 hover:border-zinc-400"}`}
                style={{ background: t.preview_bg }}
              >
                <div className="font-bold text-sm leading-tight" style={{ color: t.primary_color }}>{t.name}</div>
                <div className="text-[11px] mt-1 opacity-70 line-clamp-2" style={{ color: t.primary_color }}>{t.description}</div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDemoOpen(false)}>{t("common.cancel")}</Button>
            <Button className="bg-zinc-900 text-white" onClick={openDemo} data-testid="banks-demo-open">
              <Eye className="w-4 h-4 mr-1" /> {t("banks.openDemo")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {banks.map((b) => (
          <Link to={`/admin/banks/${b.id}`} key={b.id} className="relative bg-white border border-zinc-200 hover:border-zinc-900 hover:-translate-y-0.5 transition rounded-xl overflow-hidden" data-testid={`bank-card-${b.id}`}>
            {b.unread_messages > 0 && (
              <span
                className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 bg-red-600 text-white text-xs font-bold rounded-full px-2 py-1 shadow"
                data-testid={`bank-unread-${b.id}`}
                title="Nouveaux messages clients"
              >
                <MessageCircle className="w-3 h-3" />{b.unread_messages > 9 ? "9+" : b.unread_messages}
              </span>
            )}
            <div className="h-24 flex items-center justify-center text-white font-display text-2xl font-bold" style={{ background: b.primary_color }}>
              {b.logo_url ? <img src={b.logo_url} alt={b.name} className="h-16 object-contain" /> : b.name}
            </div>
            <div className="p-5">
              <div className="font-display text-xl font-bold">{b.name}</div>
              <div className="text-sm text-zinc-500 mt-1">/{b.slug}</div>
              {b.tagline && <p className="text-sm text-zinc-500 mt-2">{b.tagline}</p>}
              <div className="text-xs text-zinc-400 mt-3">Template: {b.template_id.replace("tpl-", "")}</div>
            </div>
          </Link>
        ))}
        {!banks.length && (
          <div className="md:col-span-2 lg:col-span-3 bg-white border border-zinc-200 rounded-xl p-10 text-center">
            <Building2 className="w-10 h-10 text-zinc-300 mx-auto mb-4" />
            <div className="font-display text-xl font-bold">{t("banks.noBanksCreated")}</div>
            <p className="text-zinc-500 mt-1 mb-4">{t("banks.launchFirstBank")}</p>
            <Link to="/admin/banks/new"><Button className="btn-pill bg-zinc-900 hover:bg-zinc-700 text-white">{t("dashboard.createBank")}</Button></Link>
          </div>
        )}
      </div>
    </div>
  );
}

function NewBank() {
  const { t } = useAdminLang();
  const [templates, setTemplates] = useState([]);
  const [subs, setSubs] = useState([]);
  const [recreationFee, setRecreationFee] = useState(null);
  const [hasPremiumAccess, setHasPremiumAccess] = useState(false);
  const [nameCheck, setNameCheck] = useState(null); // null | { available, slug, suggestions }
  const [nameChecking, setNameChecking] = useState(false);
  const nameCheckTimer = useRef(null);
  const [form, setForm] = useState({
    name: "", template_id: "tpl-classic-financial", primary_color: "#1D4ED8",
    logo_url: "", tagline: "", subscription_id: "",
    default_currency: "EUR", default_locale: "fr",
    banks_domain: "edbks.com",
  });
  const nav = useNavigate();

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data || []));
    api.get("/subscriptions", { params: { status: "active" } }).then((r) => {
      const all = r.data || [];
      setHasPremiumAccess(all.length > 0);
      setSubs(all.filter((s) => !s.bank_id));
    });
    api.get("/platform/settings/bank-recreation-fee").then((r) => setRecreationFee(r.data.value)).catch(() => {});
  }, []);

  const upd = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    if (k === "name") {
      setNameCheck(null);
      clearTimeout(nameCheckTimer.current);
      if (v.trim().length >= 2) {
        setNameChecking(true);
        nameCheckTimer.current = setTimeout(async () => {
          try {
            const { data } = await api.get("/banks/check-name", { params: { name: v.trim() } });
            setNameCheck(data);
          } catch (_) {}
          finally { setNameChecking(false); }
        }, 500);
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (nameCheck?.forbidden) {
      toast.error(nameCheck.reason || "Ce nom imite une banque réelle. Choisissez un autre nom.");
      return;
    }
    if (nameCheck && !nameCheck.available) {
      toast.error("Ce nom est déjà pris. Choisissez un autre nom ou une suggestion ci-dessous.");
      return;
    }
    try {
      const { data } = await api.post("/banks", form);
      toast.success(t("newbank.created"));
      nav(`/admin/banks/${data.id}`);
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div data-testid="admin-new-bank">
      <h1 className="font-display text-3xl font-extrabold tracking-tighter mb-2">{t("newbank.title")}</h1>
      <p className="text-zinc-500 mb-8">{t("newbank.subtitle")}</p>

      {subs.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="font-display text-lg font-bold text-amber-900">{t("newbank.noActiveSubs")}</div>
          <p className="text-sm text-amber-800 mt-1">{t("newbank.noActiveSubsHint")}</p>
          <Link to="/admin/subscriptions"><Button className="mt-4 bg-amber-600 hover:bg-amber-700 text-white" data-testid="newbank-go-subs">{t("newbank.viewSubs")}</Button></Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-8">

          {/* ── Step 0 : Domain picker ── */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6">
            <p className="text-sm font-semibold text-zinc-700 mb-1">Choix du nom de domaine</p>
            <p className="text-xs text-zinc-400 mb-4">L'URL de ta banque sera&nbsp;: <span className="font-mono-num font-bold text-zinc-700">{form.name ? form.name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") || "mabanque" : "mabanque"}.{form.banks_domain}</span></p>
            <div className="grid grid-cols-2 gap-3">
              {ALLOWED_BANK_DOMAINS.filter((d) => d === "edbks.com").map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => upd("banks_domain", d)}
                  className={`flex flex-col items-start gap-1 rounded-xl border-2 p-4 transition cursor-pointer col-span-2 ${form.banks_domain === d ? "border-zinc-900 bg-zinc-50 ring-2 ring-zinc-900/10" : "border-zinc-200 hover:border-zinc-400"}`}
                >
                  <span className="font-mono-num font-bold text-sm text-zinc-800">.{d}</span>
                  <span className="text-xs text-zinc-400">Domaine officiel de la plateforme</span>
                  {form.banks_domain === d && <span className="text-[10px] font-bold text-zinc-900 bg-zinc-200 rounded-full px-2 py-0.5 mt-1">Sélectionné ✓</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-xl p-6 space-y-4">
            <div>
              <Label>{t("newbank.subscription")}</Label>
              <Select value={form.subscription_id} onValueChange={(v) => upd("subscription_id", v)} required>
                <SelectTrigger data-testid="newbank-sub"><SelectValue placeholder={t("newbank.selectSub")} /></SelectTrigger>
                <SelectContent>
                  {subs.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.plan_name} — {fmtMoney(s.plan_price, "COINS")}
                      {(s.banks_ever_created || 0) >= 1 ? " ⚠️" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.subscription_id && (() => {
                const sel = subs.find((s) => s.id === form.subscription_id);
                if (sel && (sel.banks_ever_created || 0) >= 1) {
                  return (
                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                      <strong>{t("newbank.recreationFeeTitle")}</strong>{" "}
                      {t("newbank.recreationFeePrefix")}{" "}
                      <strong>{recreationFee ?? "…"} Coins</strong>{" "}
                      {t("newbank.recreationFeeSuffix")}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>{t("newbank.name")}</Label>
                <div className="relative">
                  <Input required value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="Yako Bank" data-testid="newbank-name" className="pr-28" />
                  {nameChecking && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-400">Vérification…</span>
                  )}
                  {!nameChecking && nameCheck && (
                    <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold px-2 py-0.5 rounded-full ${
                      nameCheck.forbidden
                        ? "bg-red-100 text-red-700"
                        : nameCheck.available
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-600"
                    }`}>
                      {nameCheck.forbidden ? "✗ Nom interdit" : nameCheck.available ? "✓ Disponible" : "✗ Déjà pris"}
                    </span>
                  )}
                </div>
                {!nameChecking && nameCheck?.forbidden && (
                  <div className="mt-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-800 leading-relaxed">
                    {nameCheck.reason}
                  </div>
                )}
                {!nameChecking && nameCheck && !nameCheck.available && !nameCheck.forbidden && nameCheck.suggestions.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-zinc-500 mb-1.5">Noms disponibles :</p>
                    <div className="flex flex-wrap gap-1.5">
                      {nameCheck.suggestions.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => { upd("name", s); }}
                          className="px-2.5 py-1 text-xs bg-zinc-100 hover:bg-zinc-900 hover:text-white border border-zinc-200 rounded-full transition-colors"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <Label>{t("newbank.primaryColor")}</Label>
                <div className="flex gap-2">
                  <Input type="color" value={form.primary_color} onChange={(e) => upd("primary_color", e.target.value)} className="w-16 h-10 p-1" data-testid="newbank-color" />
                  <Input value={form.primary_color} onChange={(e) => upd("primary_color", e.target.value)} className="flex-1 font-mono-num" />
                </div>
              </div>
            </div>
            <div><Label>{t("newbank.logo")}</Label><Input value={form.logo_url} onChange={(e) => upd("logo_url", e.target.value)} placeholder="https://…/logo.png" data-testid="newbank-logo" /></div>
            <div><Label>{t("newbank.tagline")}</Label><Input value={form.tagline} onChange={(e) => upd("tagline", e.target.value)} placeholder="La banque qui pense à toi" data-testid="newbank-tagline" /></div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>{t("newbank.currency")}</Label>
                <Select value={form.default_currency} onValueChange={(v) => upd("default_currency", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {CURRENCIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("newbank.defaultLang")}</Label>
                <Select value={form.default_locale} onValueChange={(v) => upd("default_locale", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-base font-semibold">{t("newbank.chooseTemplate")}</Label>
            {(() => {
              const groups = [...new Set(templates.map((t) => t.group || "Autre"))];
              return groups.map((g) => (
                <div key={g} className="mt-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">{g}</p>
                  {g === "Premium Signature" && !hasPremiumAccess && (
                    <p className="text-[11px] text-amber-600 font-medium mb-2 flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Templates exclusifs — abonnement actif requis
                    </p>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {templates.filter((t) => (t.group || "Autre") === g).map((t) => {
                      const locked = t.min_coins && !hasPremiumAccess;
                      return (
                        <button
                          type="button" key={t.id}
                          onClick={() => { if (locked) return; upd("template_id", t.id); upd("primary_color", t.primary_color); }}
                          className={`relative text-left rounded-xl p-4 border-2 transition ${locked ? "cursor-not-allowed opacity-60" : ""} ${!locked && form.template_id === t.id ? "border-zinc-900 ring-2 ring-zinc-900/20" : "border-zinc-200 hover:border-zinc-400"}`}
                          style={{ background: t.preview_bg }}
                          data-testid={`newbank-template-${t.id}`}
                        >
                          {locked && (
                            <div className="absolute top-2 right-2 bg-black/60 rounded-full p-0.5">
                              <Lock className="w-3 h-3 text-amber-400" />
                            </div>
                          )}
                          <div className="font-bold text-sm leading-tight" style={{ color: t.primary_color }}>{t.name}</div>
                          <div className="text-[11px] mt-1 opacity-70 leading-snug line-clamp-2" style={{ color: t.primary_color }}>{t.description}</div>
                          {locked && <div className="text-[10px] mt-1 font-bold text-amber-500">Abonnement requis</div>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
          </div>

          <Button type="submit" className="btn-pill bg-zinc-900 hover:bg-zinc-700 text-white" data-testid="newbank-submit">
            {t("newbank.create")} <ArrowUpRight className="w-4 h-4" />
          </Button>
        </form>
      )}
    </div>
  );
}

function ManageBank() {
  const { t } = useAdminLang();
  const { id } = useParams();
  const nav = useNavigate();
  const [bank, setBank] = useState(null);
  const [users, setUsers] = useState([]);
  const [cards, setCards] = useState([]);
  const [txns, setTxns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [linkLang, setLinkLang] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);

  const reload = async () => {
    try {
      const b = await api.get(`/banks/${id}`); setBank(b.data); setUnreadMsgs(b.data.unread_messages || 0);
      const u = await api.get(`/banks/${id}/users`); setUsers(u.data || []);
      const c = await api.get(`/banks/${id}/cards`); setCards(c.data || []);
      const t = await api.get(`/banks/${id}/transactions`); setTxns(t.data || []);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { reload(); api.get("/templates").then((r) => setTemplates(r.data || [])); }, [id]);
  // Poll unread client-message count so the Messages tab badge stays fresh.
  useEffect(() => {
    const poll = () => api.get(`/banks/${id}`).then((r) => setUnreadMsgs(r.data.unread_messages || 0)).catch(() => {});
    const intvl = setInterval(poll, 8000);
    return () => clearInterval(intvl);
  }, [id]);

  if (!bank) return <div className="text-zinc-400">{t("managebank.loading")}</div>;

  const deleteBank = async () => {
    try { await api.delete(`/banks/${id}`); toast.success(t("managebank.deleted")); nav("/admin/banks"); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="admin-manage-bank">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">{t("managebank.label")}</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">{bank.name}</h1>
          <p className="text-zinc-500 mt-1">/{bank.slug} · {bank.template_id.replace("tpl-", "")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={bankPublicUrl(bank.slug, "", bank.banks_domain)} target="_blank" rel="noreferrer" data-testid="bank-preview-link">
            <Button variant="outline"><Eye className="w-4 h-4 mr-1" /> {t("managebank.publicPreview")}</Button>
          </a>
          <Button variant="outline" onClick={() => { setLinkLang(bank.default_locale || "fr"); setLinkOpen(true); }} data-testid="bank-copy-link">
            <LinkIcon className="w-4 h-4 mr-1" /> {t("managebank.copyLink")}
          </Button>
          <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => setDeleteConfirm(true)} data-testid="bank-delete">
            <Trash2 className="w-4 h-4 mr-1" /> {t("managebank.delete")}
          </Button>
        </div>

        {/* Localized link picker dialog */}
        <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>{t("managebank.copyLocalizedLink")}</DialogTitle></DialogHeader>
            <p className="text-sm text-zinc-500">{t("managebank.linkDesc")}</p>
            <div className="py-2">
              <Label>{t("managebank.clientLang")}</Label>
              <Select value={linkLang} onValueChange={setLinkLang}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-zinc-100 p-2 rounded font-mono text-xs break-all">
              {`${bankPublicUrl(bank.slug, "", bank.banks_domain)}?lang=${linkLang}`}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLinkOpen(false)}>{t("common.cancel")}</Button>
              <Button className="bg-zinc-900 text-white" onClick={() => {
                navigator.clipboard?.writeText(`${bankPublicUrl(bank.slug, "", bank.banks_domain)}?lang=${linkLang}`);
                toast.success(t("managebank.linkCopied"));
                setLinkOpen(false);
              }}>
                <LinkIcon className="w-4 h-4 mr-1" /> {t("managebank.copy")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList className="h-auto flex w-full flex-wrap whitespace-normal admin-tabs-list">
          <TabsTrigger value="users" data-testid="tab-users">{t("managebank.tabUsers")}</TabsTrigger>
          <TabsTrigger value="ops" data-testid="tab-ops">{t("managebank.tabOps")}</TabsTrigger>
          <TabsTrigger value="txns" data-testid="tab-txns">{t("managebank.tabTxns")}</TabsTrigger>
          <TabsTrigger value="transfers" data-testid="tab-transfers">{t("managebank.tabTransfers")}</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages" className="relative">
            {t("managebank.tabMessages")}
            {unreadMsgs > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-red-600 text-white text-[10px] font-bold rounded-full" data-testid="tab-messages-badge">
                {unreadMsgs > 9 ? "9+" : unreadMsgs}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="credits" data-testid="tab-credits">{t("managebank.tabCredits")}</TabsTrigger>
          <TabsTrigger value="invests" data-testid="tab-invests">{t("managebank.tabInvestments")}</TabsTrigger>
          <TabsTrigger value="financings" data-testid="tab-financings">{t("managebank.tabFinancings")}</TabsTrigger>
          <TabsTrigger value="cards" data-testid="tab-cards">{t("managebank.tabCards")}</TabsTrigger>
          <TabsTrigger value="notifs" data-testid="tab-notifs">{t("managebank.tabNotifications")}</TabsTrigger>
          <TabsTrigger value="deposit" data-testid="tab-deposit">{t("managebank.tabDeposit")}</TabsTrigger>
          <TabsTrigger value="landing" data-testid="tab-landing">{t("managebank.tabLanding")}</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding">{t("managebank.tabBranding")}</TabsTrigger>
        </TabsList>

        <TabsContent value="users"><BankUsers bankId={id} users={users} reload={reload} bank={bank} /></TabsContent>
        <TabsContent value="ops"><BankingOps bankId={id} users={users} reload={reload} /></TabsContent>
        <TabsContent value="txns"><BankTxns bankId={id} users={users} txns={txns} reload={reload} /></TabsContent>
        <TabsContent value="transfers"><PendingTransfers bankId={id} /></TabsContent>
        <TabsContent value="messages"><BankMessages bankId={id} users={users} onRead={() => api.get(`/banks/${id}`).then((r) => setUnreadMsgs(r.data.unread_messages || 0)).catch(() => {})} /></TabsContent>
        <TabsContent value="credits"><BankCreditsTab bankId={id} /></TabsContent>
        <TabsContent value="invests"><BankInvestmentsTab bankId={id} /></TabsContent>
        <TabsContent value="financings"><BankFinancingsTab bankId={id} /></TabsContent>
        <TabsContent value="cards"><BankCards bankId={id} users={users} cards={cards} reload={reload} /></TabsContent>
        <TabsContent value="notifs"><BankNotifications bankId={id} users={users} /></TabsContent>
        <TabsContent value="deposit"><BankDepositSettings bank={bank} reload={reload} /></TabsContent>
        <TabsContent value="landing"><BankLanding bank={bank} reload={reload} /></TabsContent>
        <TabsContent value="branding"><BankBranding bank={bank} templates={templates} reload={reload} /></TabsContent>
      </Tabs>
      <ConfirmDialog open={deleteConfirm} title={t("managebank.deleteTitle")} message={t("managebank.deleteMsg")} onOk={() => { setDeleteConfirm(false); deleteBank(); }} onCancel={() => setDeleteConfirm(false)} />
    </div>
  );
}

function BankUsers({ bankId, users, reload, bank }) {
  const { t } = useAdminLang();
  const [open, setOpen] = useState(false);
  const [createStep, setCreateStep] = useState("form"); // "form" | "credentials"
  const [createdCreds, setCreatedCreds] = useState(null); // { name, email, phone, password, pin, loginUrl }
  const [linkOpen, setLinkOpen] = useState(false);
  const [signupLink, setSignupLink] = useState("");
  const [linkLang, setLinkLang] = useState("fr");
  const [form, setForm] = useState({ email: "", name: "", phone: "", country: "", address: "", locale: "fr" });
  const [phoneLocal, setPhoneLocal] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ currency: "EUR", locale: "fr" });
  const [stopOpen, setStopOpen] = useState(false);
  const [stopUser, setStopUser] = useState(null);
  const [stopForm, setStopForm] = useState({ stop_pct: 0, stop_message: "" });
  const [kycOpen, setKycOpen] = useState(false);
  const [kycUser, setKycUser] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const [histUser, setHistUser] = useState(null);
  const [loginHist, setLoginHist] = useState([]);
  const [resetUser, setResetUser] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [resetSending, setResetSending] = useState(false);
  const [pinResetUser, setPinResetUser] = useState(null);
  const [pinResetResult, setPinResetResult] = useState(null);
  const [pinResetSending, setPinResetSending] = useState(false);
  const [pwHistOpen, setPwHistOpen] = useState(false);
  const [pwHistory, setPwHistory] = useState([]);
  const [delPwResetConfirm, setDelPwResetConfirm] = useState(null);
  const [delAllPwResetsConfirm, setDelAllPwResetsConfirm] = useState(false);

  const selectedCountry = COUNTRIES.find((c) => c.name === form.country) || null;

  const create = async (e) => {
    e.preventDefault();
    const fullPhone = selectedCountry ? `${selectedCountry.phone} ${phoneLocal}`.trim() : phoneLocal;
    try {
      const { data } = await api.post(`/banks/${bankId}/users`, {
        ...form,
        phone: fullPhone,
      });
      const bankUrl = bankPublicUrl(bank?.slug || bankId, "", bank?.banks_domain);
      setCreatedCreds({
        name: data.name,
        email: data.email,
        phone: fullPhone,
        password: data.generated_password,
        pin: data.generated_pin,
        loginUrl: bankUrl,
      });
      setCreateStep("credentials");
      reload();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const closeCreate = () => {
    setOpen(false);
    setCreateStep("form");
    setCreatedCreds(null);
    setForm({ email: "", name: "", phone: "", country: "", address: "", locale: "fr" });
    setPhoneLocal("");
  };

  const copyAllCreds = () => {
    if (!createdCreds) return;
    const text = [
      `Lien de connexion : ${createdCreds.loginUrl}`,
      `Email : ${createdCreds.email}`,
      `Mot de passe : ${createdCreds.password}`,
      `Code PIN : ${createdCreds.pin}`,
    ].join("\n");
    navigator.clipboard?.writeText(text);
    toast.success("Identifiants copiés dans le presse-papiers");
  };
  const block = async (uid, blocked) => {
    try { await api.post(`/banks/${bankId}/users/${uid}/${blocked ? "unblock" : "block"}`); toast.success(blocked ? "Débloqué" : "Bloqué"); reload(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const validate = async (uid) => {
    try {
      await api.post(`/banks/${bankId}/users/${uid}/validate`);
      toast.success("Utilisateur validé et activé");
      reload();
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ currency: u.currency || "EUR", locale: u.locale || "fr", iban: u.iban || "", bic: u.bic || "", iban_confirmed: u.iban_confirmed || false, transfer_code_auto_delay: u.transfer_code_auto_delay ?? -1 });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!editUser) return;
    try {
      await api.patch(`/banks/${bankId}/users/${editUser.id}`, editForm);
      toast.success("Paramètres mis à jour");
      setEditOpen(false);
      reload();
    } catch (e) { toast.error(formatError(e)); }
  };
  const openStop = (u) => {
    setStopUser(u);
    setStopForm({ stop_pct: u.transfer_stop_pct || 0, stop_message: u.transfer_stop_message || "" });
    setStopOpen(true);
  };
  const saveStop = async () => {
    if (!stopUser) return;
    try {
      await api.put(`/banks/${bankId}/users/${stopUser.id}/transfer-stop`, { stop_pct: Number(stopForm.stop_pct), stop_message: stopForm.stop_message });
      toast.success("Barre de progression enregistrée");
      setStopOpen(false);
      reload();
    } catch (e) { toast.error(formatError(e)); }
  };
  const showLink = async () => {
    try {
      const { data } = await api.get(`/banks/${bankId}/signup-link`);
      const url = bankPublicUrl(data.slug, "/login", bank?.banks_domain);
      setSignupLink(url);
      setLinkLang("fr");
      setLinkOpen(true);
    } catch (e) { toast.error(formatError(e)); }
  };
  const openLoginHist = async (u) => {
    setHistUser(u);
    setLoginHist([]);
    setHistOpen(true);
    try {
      const { data } = await api.get(`/banks/${bankId}/users/${u.id}/login-history`);
      setLoginHist(data);
    } catch { setLoginHist([]); }
  };
  const deleteLoginHistEntry = async (entryId) => {
    if (!histUser) return;
    try {
      await api.delete(`/banks/${bankId}/users/${histUser.id}/login-history/${entryId}`);
      setLoginHist((prev) => prev.filter((h) => h.id !== entryId));
      toast.success("Connexion supprimée");
    } catch (e) { toast.error(formatError(e)); }
  };
  const clearLoginHist = async () => {
    if (!histUser) return;
    try {
      await api.delete(`/banks/${bankId}/users/${histUser.id}/login-history`);
      setLoginHist([]);
      toast.success("Historique de connexions supprimé");
    } catch (e) { toast.error(formatError(e)); }
  };
  const openResetPassword = (u) => { setResetUser(u); setResetResult(null); };
  const doReset = async (sendEmail) => {
    if (!resetUser) return;
    setResetSending(true);
    try {
      const { data } = await api.post(`/banks/${bankId}/users/${resetUser.id}/reset-password`, { send_email: sendEmail });
      setResetResult(data);
      toast.success(sendEmail ? t("pwreset.toastEmail") : t("pwreset.toastGen"));
    } catch (e) { toast.error(formatError(e)); }
    finally { setResetSending(false); }
  };
  const openResetPin = (u) => { setPinResetUser(u); setPinResetResult(null); };
  const doResetPin = async (sendEmail) => {
    if (!pinResetUser) return;
    setPinResetSending(true);
    try {
      const { data } = await api.post(`/banks/${bankId}/users/${pinResetUser.id}/reset-pin`, { send_email: sendEmail });
      setPinResetResult(data);
      toast.success(sendEmail ? "Nouveau PIN généré et envoyé par email" : "Nouveau PIN généré");
    } catch (e) { toast.error(formatError(e)); }
    finally { setPinResetSending(false); }
  };
  const copyPassword = (pw) => {
    navigator.clipboard?.writeText(pw);
    toast.success(t("pwreset.copied"));
  };
  const loadPwHistory = async () => {
    try {
      const { data } = await api.get(`/banks/${bankId}/password-resets`);
      setPwHistory(data);
    } catch (e) { toast.error(formatError(e)); }
  };
  const openPwHistory = async () => {
    setPwHistOpen(true);
    await loadPwHistory();
  };
  const deletePwReset = async (id) => {
    try {
      await api.delete(`/banks/${bankId}/password-resets/${id}`);
      toast.success(t("pwreset.entryDeleted"));
      setDelPwResetConfirm(null);
      await loadPwHistory();
    } catch (e) { toast.error(formatError(e)); }
  };
  const deleteAllPwResets = async () => {
    try {
      const { data } = await api.delete(`/banks/${bankId}/password-resets`);
      toast.success(`${data.deleted || 0} ${t("pwreset.deletedCount")}`);
      setDelAllPwResetsConfirm(false);
      await loadPwHistory();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div>
      <div className="mb-4 flex justify-between items-center flex-wrap gap-3">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Users2 className="w-5 h-5" /> Utilisateurs finaux ({users.length})</h2>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={openPwHistory} data-testid="bu-pw-history"><History className="w-4 h-4 mr-1" /> {t("pwreset.button")}</Button>
          <Button variant="outline" onClick={showLink} data-testid="bu-signup-link"><LinkIcon className="w-4 h-4 mr-1" /> Liens publics</Button>
          <Dialog open={open} onOpenChange={(v) => { if (!v) closeCreate(); else setOpen(true); }}>
            <DialogTrigger asChild><Button className="bg-zinc-900 hover:bg-zinc-700 text-white" data-testid="bu-new"><Plus className="w-4 h-4 mr-1" /> Créer manuellement</Button></DialogTrigger>
            <DialogContent className="max-w-xl">
              {createStep === "form" ? (
                <>
                  <DialogHeader>
                    <DialogTitle>Nouvel utilisateur</DialogTitle>
                    <p className="text-xs text-zinc-500 mt-1">Le mot de passe et le Code PIN seront générés automatiquement et envoyés par email.</p>
                  </DialogHeader>
                  <form onSubmit={create} className="space-y-3 pt-1">
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="md:col-span-2"><Label>Nom complet *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Jean Dupont" data-testid="bu-name" /></div>
                      <div className="md:col-span-2"><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="jean@exemple.com" data-testid="bu-email" /></div>
                      <div>
                        <Label>Pays *</Label>
                        <Select value={form.country} onValueChange={(v) => { setForm({ ...form, country: v }); setPhoneLocal(""); }} required>
                          <SelectTrigger data-testid="bu-country"><SelectValue placeholder="Sélectionner un pays" /></SelectTrigger>
                          <SelectContent className="max-h-72">
                            {COUNTRIES.map((c) => (
                              <SelectItem key={c.name} value={c.name}>{c.flag} {c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Téléphone *</Label>
                        <div className="flex gap-1">
                          <div className="flex items-center px-3 bg-zinc-100 border border-zinc-200 rounded-lg text-sm font-mono whitespace-nowrap min-w-[70px]">
                            {selectedCountry ? <>{selectedCountry.flag} {selectedCountry.phone}</> : "—"}
                          </div>
                          <Input
                            value={phoneLocal}
                            onChange={(e) => setPhoneLocal(e.target.value)}
                            required
                            placeholder="6 12 34 56"
                            disabled={!selectedCountry}
                            data-testid="bu-phone"
                          />
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <Label>Adresse</Label>
                        <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="12 Rue de la Paix, 75001 Paris" data-testid="bu-address" />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Langue du client</Label>
                        <Select value={form.locale} onValueChange={(v) => setForm({ ...form, locale: v })}>
                          <SelectTrigger data-testid="bu-locale"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" type="button" onClick={closeCreate}>Annuler</Button>
                      <Button type="submit" className="bg-zinc-900 text-white" data-testid="bu-submit">Créer le compte</Button>
                    </DialogFooter>
                  </form>
                </>
              ) : createdCreds ? (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-green-500" /> Compte créé avec succès</DialogTitle>
                    <p className="text-xs text-zinc-500 mt-1">Un email avec ces informations a été envoyé automatiquement à <strong>{createdCreds.email}</strong>.</p>
                  </DialogHeader>
                  <div className="space-y-2 py-1">
                    <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 text-sm space-y-2">
                      {[
                        ["Nom", createdCreds.name],
                        ["Email", createdCreds.email],
                        ["Téléphone", createdCreds.phone],
                        ["Lien connexion", createdCreds.loginUrl],
                      ].map(([label, value]) => (
                        <div key={label} className="flex gap-2">
                          <span className="text-zinc-500 w-28 shrink-0">{label} :</span>
                          <span className="font-medium text-zinc-900 break-all text-xs">{value}</span>
                        </div>
                      ))}
                      <div className="border-t border-zinc-200 pt-2 mt-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-500 w-28 shrink-0 text-sm">Mot de passe :</span>
                          <span className="font-mono font-bold text-zinc-900 flex-1">{createdCreds.password}</span>
                          <button onClick={() => { navigator.clipboard?.writeText(createdCreds.password); toast.success("Mot de passe copié"); }} className="p-1.5 rounded-lg hover:bg-zinc-200 transition"><Copy className="w-3.5 h-3.5" /></button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-500 w-28 shrink-0 text-sm">Code PIN :</span>
                          <span className="font-mono font-bold text-zinc-900 text-lg tracking-widest flex-1">{createdCreds.pin}</span>
                          <button onClick={() => { navigator.clipboard?.writeText(createdCreds.pin); toast.success("Code PIN copié"); }} className="p-1.5 rounded-lg hover:bg-zinc-200 transition"><Copy className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={copyAllCreds}><Copy className="w-4 h-4 mr-1" /> Tout copier</Button>
                    <Button className="bg-zinc-900 text-white" onClick={closeCreate}>Fermer</Button>
                  </DialogFooter>
                </>
              ) : null}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Liens publics de la banque</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500">Choisis la langue du client — elle sera pré-sélectionnée à l'ouverture du lien.</p>
          <div className="py-2">
            <Label>Langue du client</Label>
            <Select value={linkLang} onValueChange={setLinkLang}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Lien de connexion */}
          <div className="space-y-1.5 mt-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-zinc-500">Lien de connexion</Label>
              <button
                type="button"
                className="text-xs text-zinc-700 hover:text-zinc-900 flex items-center gap-1"
                onClick={() => {
                  const url = signupLink ? `${signupLink.replace(/\/signup$/, "/login")}?lang=${linkLang}` : "";
                  navigator.clipboard?.writeText(url);
                  toast.success("Lien de connexion copié");
                }}
                data-testid="login-link-copy"
              >
                <Copy className="w-3.5 h-3.5" /> Copier
              </button>
            </div>
            <div className="bg-zinc-100 p-2 rounded font-mono text-xs break-all" data-testid="login-link-text">
              {signupLink ? `${signupLink.replace(/\/signup$/, "/login")}?lang=${linkLang}` : ""}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog — modifier devise / langue */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Paramètres de {editUser?.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-400 pb-1">Modifiez la devise d'affichage, la langue, ou personnalisez l'IBAN et le BIC attribués à ce client.</p>
          <div className="space-y-4 py-2">
            <div>
              <Label>Devise d'affichage</Label>
              <Select value={editForm.currency} onValueChange={(v) => setEditForm({ ...editForm, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {CURRENCIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Langue d'affichage</Label>
              <Select value={editForm.locale} onValueChange={(v) => setEditForm({ ...editForm, locale: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="border-t border-zinc-100 pt-3 space-y-3">
              <p className="text-xs text-zinc-400">Laisser vide pour conserver l'IBAN généré automatiquement selon le pays de la banque.</p>
              <div><Label>IBAN (personnalisé)</Label><Input value={editForm.iban} onChange={(e) => setEditForm({ ...editForm, iban: e.target.value.toUpperCase() })} placeholder="FR76 3000 6000 0112 3456 7890 189" /></div>
              <div><Label>BIC / SWIFT</Label><Input value={editForm.bic} onChange={(e) => setEditForm({ ...editForm, bic: e.target.value.toUpperCase() })} placeholder="BNPAFRPPXXX" /></div>
              <div className="md:col-span-2 flex items-center gap-3">
                <Switch
                  checked={!!editForm.iban_confirmed}
                  onCheckedChange={(v) => setEditForm({ ...editForm, iban_confirmed: v })}
                  data-testid="user-iban-confirmed"
                />
                <label className="text-sm font-medium">IBAN confirmé (révèle les 4 derniers chiffres)</label>
              </div>
            </div>
            <div className="border-t border-zinc-100 pt-3 space-y-3">
              <div className="flex items-center gap-3">
                <Switch
                  checked={(editForm.transfer_code_auto_delay ?? -1) >= 0}
                  onCheckedChange={(v) => setEditForm({ ...editForm, transfer_code_auto_delay: v ? 30 : -1 })}
                  data-testid="user-auto-code"
                />
                <label className="text-sm font-medium">Envoyer le code de virement automatiquement</label>
              </div>
              {(editForm.transfer_code_auto_delay ?? -1) >= 0 ? (
                <div>
                  <Label>Délai avant envoi automatique (secondes)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editForm.transfer_code_auto_delay}
                    onChange={(e) => setEditForm({ ...editForm, transfer_code_auto_delay: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                    placeholder="Ex : 30"
                    data-testid="user-auto-code-delay"
                  />
                  <p className="text-xs text-zinc-400 mt-1">Le client recevra le code dans sa cloche {editForm.transfer_code_auto_delay === 0 ? "immédiatement" : `${editForm.transfer_code_auto_delay} s`} après avoir lancé un virement.</p>
                </div>
              ) : (
                <p className="text-xs text-zinc-400">Le code ne sera <strong>jamais</strong> envoyé automatiquement. Vous l'enverrez manuellement depuis la liste des virements.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annuler</Button>
            <Button className="bg-zinc-900 text-white" onClick={saveEdit}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog — Barre de progression du virement */}
      <Dialog open={stopOpen} onOpenChange={setStopOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Barre de progression du virement — {stopUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-xs text-zinc-500">Quand cet utilisateur valide un virement avec son code, la barre de progression s'arrêtera à ce pourcentage avec le message ci-dessous. Tant que vous ne changez pas cette valeur, chaque tentative se bloquera au même endroit. <strong>0 = pas de blocage</strong> (virement instantané). <strong>100 = le virement se complète normalement.</strong></p>
            <div>
              <Label>Pourcentage d'arrêt de la barre (%)</Label>
              <Input type="number" min={0} max={100} value={stopForm.stop_pct} onChange={(e) => setStopForm({ ...stopForm, stop_pct: e.target.value })} placeholder="Ex : 75" />
              <p className="text-xs text-zinc-400 mt-1">Ex : 75 → la barre s'arrête à 75% avec le message ci-dessous</p>
            </div>
            <div>
              <Label>Message affiché au client lors du blocage</Label>
              <Input value={stopForm.stop_message} onChange={(e) => setStopForm({ ...stopForm, stop_message: e.target.value })} placeholder="Votre virement est en cours de vérification par notre équipe sécurité." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStopOpen(false)}>Annuler</Button>
            <Button className="bg-zinc-900 text-white" onClick={saveStop}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog — Visionner les documents KYC */}
      <Dialog open={kycOpen} onOpenChange={setKycOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Documents KYC — {kycUser?.name}</DialogTitle></DialogHeader>
          {kycUser && (
            <div className="space-y-4 py-2">
              <div className="text-xs text-zinc-500 bg-zinc-50 p-3 rounded-lg">
                <div><span className="font-bold">Email :</span> {kycUser.email}</div>
                <div><span className="font-bold">Pays :</span> {kycUser.country || "—"}</div>
                <div><span className="font-bold">Type de pièce :</span> {
                  { id_card: "Carte d'identité", passport: "Passeport", driver_license: "Permis de conduire" }[kycUser.kyc_id_type] || "—"
                }</div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  { key: "kyc_id_recto", label: "Recto pièce d'identité" },
                  ...(kycUser.kyc_id_type !== "passport" ? [{ key: "kyc_id_verso", label: "Verso pièce d'identité" }] : []),
                  { key: "kyc_selfie", label: "Selfie avec pièce" },
                  { key: "kyc_address_proof", label: "Justificatif de domicile" },
                ].map((doc) => {
                  const url = kycUser[doc.key];
                  if (!url) return (
                    <div key={doc.key} className="border border-dashed border-zinc-200 rounded-lg p-4 text-center text-xs text-zinc-400">
                      <div className="font-bold mb-1">{doc.label}</div>Non fourni
                    </div>
                  );
                  const isImage = url.startsWith("data:image");
                  const isPdf = url.startsWith("data:application/pdf");
                  return (
                    <div key={doc.key} className="border border-zinc-200 rounded-lg overflow-hidden">
                      <div className="px-3 py-2 bg-zinc-50 text-xs font-bold text-zinc-700 border-b">{doc.label}</div>
                      {isImage && <img src={url} alt={doc.label} className="w-full h-48 object-contain bg-zinc-100" />}
                      {isPdf && (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="block p-6 text-center text-sm font-bold text-blue-600 hover:bg-blue-50">📄 Ouvrir le PDF</a>
                      )}
                      {!isImage && !isPdf && (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="block p-6 text-center text-sm font-bold text-blue-600 hover:bg-blue-50">📎 Ouvrir le fichier</a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setKycOpen(false)}>Fermer</Button>
            {kycUser?.status === "pending" && (
              <Button className="bg-emerald-600 text-white" onClick={() => { validate(kycUser.id); setKycOpen(false); }}>Valider le compte</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog — Historique de connexions */}
      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Historique de connexions — {histUser?.name}</DialogTitle></DialogHeader>
          {loginHist.length === 0 ? (
            <p className="text-sm text-zinc-400 py-4 text-center">Aucune connexion enregistrée.</p>
          ) : (
            <div className="space-y-2 py-2">
              {loginHist.map((h, i) => (
                <div key={h.id || i} className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg text-sm">
                  <div className="flex-1">
                    <div className="font-semibold text-zinc-800">{new Date(h.created_at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}</div>
                    <div className="text-xs text-zinc-400 mt-0.5">IP: {h.ip || "—"}</div>
                    <div className="text-xs text-zinc-400 truncate max-w-xs">{h.user_agent || "—"}</div>
                  </div>
                  {i === 0 && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">Récent</span>}
                  <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600 hover:bg-red-50 px-2" onClick={() => deleteLoginHistEntry(h.id)} title="Supprimer cette connexion" data-testid={`del-login-hist-${h.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter className="flex-row justify-between gap-2">
            {loginHist.length > 0 && (
              <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={clearLoginHist} data-testid="clear-login-hist">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Tout supprimer
              </Button>
            )}
            <Button variant="outline" onClick={() => setHistOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-4">Nom / Email</th><th className="text-left p-4">Pays</th><th className="text-right p-4">Solde</th><th className="text-center p-4">Statut</th><th className="p-4"></th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-zinc-100">
                <td className="p-4">
                  <div className="font-semibold">{u.name}</div>
                  <div className="text-xs text-zinc-400">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div>
                </td>
                <td className="p-4 text-zinc-600">{u.country || "—"}</td>
                <td className="p-4 text-right font-mono-num">{fmtMoney(u.balance, u.currency || "EUR")}</td>
                <td className="p-4 text-center">
                  <Badge className={
                    u.status === "blocked" ? "bg-red-100 text-red-700 border-red-200" :
                    u.status === "pending" ? "bg-amber-100 text-amber-700 border-amber-200" :
                    "bg-emerald-100 text-emerald-700 border-emerald-200"
                  }>{u.status === "pending" ? "En attente" : (u.status || "active")}</Badge>
                </td>
                <td className="p-4 text-right space-x-1">
                  {u.kyc_id_recto && (
                    <Button size="sm" variant="outline" onClick={() => { setKycUser(u); setKycOpen(true); }} className="text-blue-600 border-blue-200 hover:bg-blue-50">
                      KYC
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openLoginHist(u)} className="text-indigo-600 border-indigo-200 hover:bg-indigo-50" title="Historique de connexions">
                    Connexions
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(u)} className="text-zinc-600 border-zinc-200 hover:bg-zinc-50" title="Devise, langue, IBAN…">
                    Paramètres
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openStop(u)} className="text-violet-600 border-violet-200 hover:bg-violet-50" title="Barre de progression du virement">
                    {u.transfer_stop_pct > 0 ? `Stop ${u.transfer_stop_pct}%` : "Progression"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openResetPassword(u)} className="text-orange-600 border-orange-200 hover:bg-orange-50" title={t("pwreset.title")} data-testid={`bu-reset-pw-${u.id}`}>
                    <KeyRound className="w-3.5 h-3.5 mr-1" /> MDP
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openResetPin(u)} className="text-purple-600 border-purple-200 hover:bg-purple-50" title="Régénérer le Code PIN" data-testid={`bu-reset-pin-${u.id}`}>
                    <KeyRound className="w-3.5 h-3.5 mr-1" /> PIN
                  </Button>
                  {u.status === "pending" ? (
                    <Button size="sm" onClick={() => validate(u.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`bu-validate-${u.id}`}>Valider</Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => block(u.id, u.status === "blocked")} className={u.status === "blocked" ? "" : "text-amber-600"} data-testid={`bu-block-${u.id}`}>
                      {u.status === "blocked" ? "Débloquer" : "Bloquer"}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {!users.length && <tr><td colSpan={5} className="p-8 text-center text-zinc-400">Aucun utilisateur.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      {/* Password reset dialog */}
      <Dialog open={!!resetUser} onOpenChange={(o) => { if (!o) { setResetUser(null); setResetResult(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("pwreset.title")} — {resetUser?.name}</DialogTitle></DialogHeader>
          {!resetResult ? (
            <div className="space-y-3 py-2">
              <p className="text-sm text-zinc-600">{t("pwreset.confirm")} <strong>{resetUser?.email}</strong>{t("pwreset.confirmEnd")}</p>
              <div className="flex flex-col gap-2 pt-2">
                <Button onClick={() => doReset(false)} disabled={resetSending} className="bg-zinc-900 hover:bg-zinc-700 text-white">
                  <KeyRound className="w-4 h-4 mr-1.5" /> {t("pwreset.generate")}
                </Button>
                <Button onClick={() => doReset(true)} disabled={resetSending} variant="outline" className="border-zinc-300">
                  <MessageCircle className="w-4 h-4 mr-1.5" /> {t("pwreset.generateEmail")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                ✓ {resetResult.sent_by_email ? t("pwreset.generatedEmail") : t("pwreset.generated")}
              </p>
              <div>
                <Label className="text-xs">{t("pwreset.newPassword")}</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={resetResult.new_password} readOnly className="font-mono-num font-bold" />
                  <Button onClick={() => copyPassword(resetResult.new_password)} variant="outline">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-zinc-500 mt-2">{t("pwreset.hint")}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetUser(null); setResetResult(null); }}>{t("pwreset.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIN reset dialog */}
      <Dialog open={!!pinResetUser} onOpenChange={(o) => { if (!o) { setPinResetUser(null); setPinResetResult(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Régénérer le Code PIN — {pinResetUser?.name}</DialogTitle></DialogHeader>
          {!pinResetResult ? (
            <div className="space-y-3 py-2">
              <p className="text-sm text-zinc-600">Générer un nouveau Code PIN 4 chiffres pour <strong>{pinResetUser?.email}</strong> ?</p>
              <div className="flex flex-col gap-2 pt-2">
                <Button onClick={() => doResetPin(false)} disabled={pinResetSending} className="bg-purple-700 hover:bg-purple-800 text-white">
                  <KeyRound className="w-4 h-4 mr-1.5" /> Générer un nouveau PIN
                </Button>
                <Button onClick={() => doResetPin(true)} disabled={pinResetSending} variant="outline" className="border-zinc-300">
                  <MessageCircle className="w-4 h-4 mr-1.5" /> Générer et envoyer par email
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                ✓ {pinResetResult.sent_by_email ? "Nouveau PIN généré et envoyé par email" : "Nouveau PIN généré"}
              </p>
              <div>
                <Label className="text-xs">Nouveau Code PIN</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={pinResetResult.new_pin} readOnly className="font-mono font-bold text-2xl tracking-widest text-center" />
                  <Button onClick={() => { navigator.clipboard?.writeText(pinResetResult.new_pin); toast.success("PIN copié"); }} variant="outline">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-zinc-500 mt-2">Communiquez ce PIN au client de manière sécurisée.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPinResetUser(null); setPinResetResult(null); }}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password reset history dialog */}
      <Dialog open={pwHistOpen} onOpenChange={setPwHistOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2">
              <span>{t("pwreset.histTitle")}</span>
              {pwHistory.length > 0 && (
                <Button size="sm" variant="outline" onClick={() => setDelAllPwResetsConfirm(true)} className="text-red-600 border-red-200 hover:bg-red-50 mr-6">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> {t("pwreset.deleteAll")}
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1">
            {!pwHistory.length ? (
              <div className="text-center text-zinc-400 py-10 text-sm">{t("pwreset.histEmpty")}</div>
            ) : (
              <div className="space-y-2">
                {pwHistory.map((h) => (
                  <div key={h.id} className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{h.user_name}</div>
                      <div className="text-xs text-zinc-500 truncate">{h.user_email}</div>
                      <div className="text-xs text-zinc-400 mt-0.5">
                        {new Date(h.created_at).toLocaleString("fr-FR")}
                        {h.sent_by_email && <span className="ml-2 text-emerald-600">· {t("pwreset.sentByEmail")}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <code className="font-mono-num text-xs bg-white border border-zinc-200 rounded px-2 py-1">{h.new_password}</code>
                      <Button size="sm" variant="ghost" onClick={() => copyPassword(h.new_password)} title={t("pwreset.copyTitle")}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDelPwResetConfirm(h.id)} className="text-red-500 hover:bg-red-50" title={t("pwreset.deleteTitle")}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPwHistOpen(false)}>{t("pwreset.close")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delPwResetConfirm} onOpenChange={(o) => { if (!o) setDelPwResetConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("pwreset.delEntryTitle")}</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600 py-1">{t("pwreset.delEntryMsg")}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelPwResetConfirm(null)}>{t("pwreset.cancel")}</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={() => deletePwReset(delPwResetConfirm)}>{t("pwreset.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={delAllPwResetsConfirm} onOpenChange={(o) => { if (!o) setDelAllPwResetsConfirm(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("pwreset.delAllTitle")}</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600 py-1">{t("pwreset.delAllMsg")}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelAllPwResetsConfirm(false)}>{t("pwreset.cancel")}</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={deleteAllPwResets}>{t("pwreset.deleteAll")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const DEFAULT_PHYS_TYPES = [
  { id: "metal", name: "Metal", price: 29.00, features: ["Carte dorée exclusive", "Cashback 0,5%", "Protection acheteur", "Support standard"] },
  { id: "premium", name: "Premium", price: 49.00, features: ["Carte plastique premium", "Cashback 1%", "Assurance voyage standard", "Support client prioritaire"] },
  { id: "gold", name: "Gold", price: 99.00, features: ["Carte en métal brossé", "Cashback 2% illimité", "Assurance voyage premium", "Accès salons VIP aéroports", "Assistance dédiée 24/7"] },
];

const PHYS_GRADIENTS = {
  metal: "linear-gradient(135deg, #1c1c1e 0%, #3a3a3c 50%, #6b7280 100%)",
  premium: "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)",
  gold: "linear-gradient(135deg, #78350f 0%, #b45309 50%, #fbbf24 100%)",
};

function BankCards({ bankId, users, cards, reload }) {
  const [form, setForm] = useState({ bank_user_id: "", holder_name: "", card_type: "visa", color: "#0f172a" });
  const [editCard, setEditCard] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [physTypes, setPhysTypes] = useState(DEFAULT_PHYS_TYPES);
  const [editPhysType, setEditPhysType] = useState(null);
  const [physEditForm, setPhysEditForm] = useState({ name: "", price: 0, features: "" });
  const [physLoading, setPhysLoading] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [physOrderDeleteTarget, setPhysOrderDeleteTarget] = useState(null);
  const [expandedMsgCard, setExpandedMsgCard] = useState(null);
  const [trackingMsgForm, setTrackingMsgForm] = useState({ preparation: "", shipped: "", transit: "", delivered: "" });
  const [trackingMsgLoading, setTrackingMsgLoading] = useState(false);
  const [copiedTrackingId, setCopiedTrackingId] = useState(null);

  useEffect(() => {
    api.get(`/banks/${bankId}/physical-card-types`).then((r) => setPhysTypes(r.data)).catch(() => {});
  }, [bankId]);

  const savePhysType = async () => {
    setPhysLoading(true);
    try {
      const updated = physTypes.map((t) => t.id === editPhysType.id
        ? { ...t, name: physEditForm.name.trim() || t.name, price: Number(physEditForm.price), features: physEditForm.features.split("\n").map((s) => s.trim()).filter(Boolean) }
        : t
      );
      await api.put(`/banks/${bankId}/physical-card-types`, updated);
      setPhysTypes(updated);
      setEditPhysType(null);
      toast.success("Type de carte mis à jour");
    } catch (err) { toast.error(formatError(err)); }
    finally { setPhysLoading(false); }
  };

  const resetPhysTypes = async () => {
    setResetConfirm(false);
    try {
      await api.put(`/banks/${bankId}/physical-card-types`, DEFAULT_PHYS_TYPES);
      setPhysTypes(DEFAULT_PHYS_TYPES);
      toast.success("Types de cartes réinitialisés");
    } catch (err) { toast.error(formatError(err)); }
  };

  const copyTracking = (cardId, num) => {
    navigator.clipboard?.writeText(num);
    setCopiedTrackingId(cardId);
    setTimeout(() => setCopiedTrackingId(null), 1500);
  };

  const openMsgEditor = (c) => {
    setExpandedMsgCard(expandedMsgCard === c.id ? null : c.id);
    setTrackingMsgForm({
      preparation: c.tracking_messages?.preparation || "",
      shipped: c.tracking_messages?.shipped || "",
      transit: c.tracking_messages?.transit || "",
      delivered: c.tracking_messages?.delivered || "",
    });
  };

  const saveTrackingMessages = async (cardId) => {
    setTrackingMsgLoading(true);
    try {
      await api.patch(`/banks/${bankId}/cards/${cardId}/tracking-messages`, trackingMsgForm);
      toast.success("Messages mis à jour");
      setExpandedMsgCard(null);
      reload();
    } catch (err) { toast.error(formatError(err)); }
    finally { setTrackingMsgLoading(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.bank_user_id) return toast.error("Sélectionne un utilisateur");
    try { await api.post(`/banks/${bankId}/cards`, form); toast.success("Carte émise"); setForm({ bank_user_id: "", holder_name: "", card_type: "visa", color: "#0f172a" }); reload(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const openEdit = (c) => {
    setEditCard(c);
    setEditForm({ holder_name: c.holder_name, cvv: c.cvv || "", number_full: c.number_full || "", expiry: c.expiry || "", card_type: c.card_type || "visa", color: c.color || "#0f172a", limit_daily: c.limit_daily ?? 1000, limit_weekly: c.limit_weekly ?? 5000, limit_monthly: c.limit_monthly ?? 10000, card_confirmed: c.card_confirmed || false });
  };

  const saveEdit = async () => {
    setEditLoading(true);
    try {
      await api.put(`/banks/${bankId}/cards/${editCard.id}`, editForm);
      toast.success("Carte mise à jour");
      setEditCard(null);
      reload();
    } catch (err) { toast.error(formatError(err)); }
    finally { setEditLoading(false); }
  };

  const deleteCard = async () => {
    const c = deleteTarget;
    setDeleteTarget(null);
    if (!c) return;
    try {
      await api.delete(`/banks/${bankId}/cards/${c.id}`);
      toast.success("Carte supprimée");
      reload();
    } catch (err) { toast.error(formatError(err)); }
  };

  const deletePhysicalOrder = async () => {
    const c = physOrderDeleteTarget;
    setPhysOrderDeleteTarget(null);
    if (!c) return;
    try {
      await api.delete(`/banks/${bankId}/cards/${c.id}/physical-order`);
      toast.success("Commande physique supprimée — la carte virtuelle est conservée");
      reload();
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div>
      <h2 className="font-display text-xl font-bold mb-4">Cartes virtuelles ({cards.length})</h2>
      <form onSubmit={create} className="bg-white border border-zinc-200 rounded-xl p-4 mb-4 grid md:grid-cols-4 gap-3 items-end">
        <div>
          <Label>Utilisateur</Label>
          <Select value={form.bank_user_id} onValueChange={(v) => setForm({ ...form, bank_user_id: v })}>
            <SelectTrigger data-testid="card-user"><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Nom sur la carte</Label><Input value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value.toUpperCase() })} placeholder="JEAN DUPONT" data-testid="card-holder" /></div>
        <div>
          <Label>Réseau</Label>
          <Select value={form.card_type} onValueChange={(v) => setForm({ ...form, card_type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="visa">VISA</SelectItem>
              <SelectItem value="mastercard">Mastercard</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" className="bg-zinc-900 hover:bg-zinc-700 text-white" data-testid="card-submit">Émettre</Button>
      </form>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map((c) => {
          const isVisa = (c.card_type || "visa") === "visa";
          const cardColor = c.color || "#1e293b";
          return (
            <div key={c.id} className="rounded-2xl p-5 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${cardColor} 0%, ${cardColor}cc 100%)` }}>
              <div className="flex justify-between items-center mb-4">
                <div className="text-[10px] uppercase tracking-[0.18em] opacity-60">{c.status === "blocked" ? "🔒 Bloquée" : "Virtual Card"}</div>
                {isVisa ? (
                  <span className="font-serif font-black italic text-base opacity-90">VISA</span>
                ) : (
                  <div className="flex">
                    <div className="w-5 h-5 rounded-full bg-red-500 opacity-90" />
                    <div className="w-5 h-5 rounded-full bg-yellow-400 opacity-90 -ml-2" />
                  </div>
                )}
              </div>
              <div className="font-mono text-sm tracking-widest mt-2 opacity-90">{c.number_masked}</div>
              <div className="mt-3 flex justify-between text-xs">
                <div><div className="opacity-50">Titulaire</div><div className="font-semibold">{c.holder_name}</div></div>
                <div className="text-right"><div className="opacity-50">Exp.</div><div className="font-semibold">{c.expiry}</div></div>
              </div>
              {c.bank_user_name && <div className="mt-1 text-[11px] opacity-60">Client : {c.bank_user_name}</div>}
              <div className="mt-3 flex justify-between items-center text-xs">
                <div className="opacity-60">CVV : {c.cvv || "—"}</div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(c)} className="bg-white/20 hover:bg-white/30 text-white rounded-lg px-3 py-1 text-xs font-semibold transition">Modifier</button>
                  <button onClick={() => setDeleteTarget(c)} className="bg-red-500/30 hover:bg-red-500/50 text-white rounded-lg px-3 py-1 text-xs font-semibold transition">Supprimer</button>
                </div>
              </div>
            </div>
          );
        })}
        {!cards.length && <div className="md:col-span-3 text-center text-zinc-400 py-8">Aucune carte.</div>}
      </div>

      {/* Section — Commandes de cartes physiques */}
      {cards.some((c) => c.physical_ordered) && (
        <div className="mt-8">
          <h2 className="font-display text-xl font-bold mb-4">Commandes de cartes physiques ({cards.filter((c) => c.physical_ordered).length})</h2>
          <div className="rounded-xl border border-zinc-200 overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-2 text-left">Client</th>
                  <th className="px-4 py-2 text-left">Carte (n°)</th>
                  <th className="px-4 py-2 text-left">N° suivi</th>
                  <th className="px-4 py-2 text-left">Type physique</th>
                  <th className="px-4 py-2 text-left">Réseau</th>
                  <th className="px-4 py-2 text-left">Suivi</th>
                  <th className="px-4 py-2 text-left"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {cards.filter((c) => c.physical_ordered).map((c) => (
                  <>
                  <tr key={c.id} className="bg-white hover:bg-zinc-50">
                    <td className="px-4 py-3 font-medium">{c.bank_user_name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{c.number_masked}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-zinc-500">{c.tracking_number || "—"}</span>
                        {c.tracking_number && (
                          <button onClick={() => copyTracking(c.id, c.tracking_number)} className="text-zinc-400 hover:text-zinc-600 transition-colors p-0.5" title="Copier le numéro de suivi">
                            {copiedTrackingId === c.id ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 capitalize">{c.physical_type || "—"}</td>
                    <td className="px-4 py-3 uppercase">{c.physical_network || "—"}</td>
                    <td className="px-4 py-3">
                      <select
                        value={c.tracking_status || "preparation"}
                        onChange={async (e) => {
                          await api.patch(`/banks/${bankId}/cards/${c.id}/tracking`, { status: e.target.value });
                          reload();
                        }}
                        className="text-xs border border-zinc-200 rounded px-2 py-1"
                      >
                        <option value="preparation">En préparation</option>
                        <option value="shipped">Expédié</option>
                        <option value="transit">En transit</option>
                        <option value="delivered">Livré</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 flex-wrap">
                        <Button size="sm" variant="ghost" className="text-zinc-600 text-xs" onClick={() => openMsgEditor(c)}>
                          {expandedMsgCard === c.id ? "Fermer" : "Messages"}
                        </Button>
                        {c.tracking_status === "delivered" && (
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 text-xs" onClick={() => setPhysOrderDeleteTarget(c)}>Supprimer</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedMsgCard === c.id && (
                    <tr key={`${c.id}-msg`} className="bg-zinc-50">
                      <td colSpan={7} className="px-4 py-4 border-t border-zinc-200">
                        <p className="text-xs font-semibold text-zinc-500 uppercase mb-3">Messages personnalisés par étape</p>
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            { key: "preparation", label: "En préparation" },
                            { key: "shipped", label: "Expédié" },
                            { key: "transit", label: "En transit" },
                            { key: "delivered", label: "Livré" },
                          ].map(({ key, label }) => (
                            <div key={key}>
                              <label className="text-xs font-semibold text-zinc-500 uppercase mb-1 block">{label}</label>
                              <textarea
                                value={trackingMsgForm[key]}
                                onChange={(e) => setTrackingMsgForm((prev) => ({ ...prev, [key]: e.target.value }))}
                                placeholder="Message personnalisé pour cette étape…"
                                rows={2}
                                className="w-full text-xs border border-zinc-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-zinc-300 bg-white"
                              />
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" disabled={trackingMsgLoading} onClick={() => saveTrackingMessages(c.id)}>
                            {trackingMsgLoading ? "Enregistrement…" : "Enregistrer les messages"}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setExpandedMsgCard(null)}>Annuler</Button>
                        </div>
                      </td>
                    </tr>
                  )}
                  </>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}

      {/* Section — Configuration cartes physiques */}
      <div className="mt-8">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="font-display text-xl font-bold">Cartes physiques</h2>
          <Button size="sm" variant="outline" onClick={() => setResetConfirm(true)} className="text-xs">
            Réinitialiser aux valeurs par défaut
          </Button>
        </div>
        <p className="text-xs text-zinc-500 mb-4">Configurez le nom, prix et avantages de chaque type de carte physique. Les clients voient ces informations lors de la commande.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {physTypes.map((pt) => (
            <div key={pt.id} className="rounded-2xl overflow-hidden border border-zinc-200 shadow-sm">
              <div style={{ background: PHYS_GRADIENTS[pt.id] || "#1e293b", padding: "20px 18px 16px", position: "relative" }}>
                <div className="flex justify-between items-start">
                  <div>
                    <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 4 }}>Carte physique</div>
                    <div style={{ color: "#fff", fontWeight: 900, fontSize: "1.2rem", fontFamily: "'Syne', sans-serif", letterSpacing: "0.05em" }}>{pt.name}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.6rem", textTransform: "uppercase" }}>Prix</div>
                    <div style={{ color: "#fff", fontWeight: 800, fontSize: "1.1rem" }}>{Number(pt.price).toFixed(2)} €</div>
                  </div>
                </div>
                <div style={{ marginTop: 16, display: "flex", gap: 4 }}>
                  <div style={{ width: 32, height: 22, background: "rgba(255,255,255,0.2)", borderRadius: 3 }} />
                  {pt.id === "metal" ? (
                    <span style={{ color: "#fff", fontWeight: 900, fontSize: "0.85rem", fontStyle: "italic", fontFamily: "serif", marginLeft: "auto" }}>VISA</span>
                  ) : (
                    <div style={{ display: "flex", marginLeft: "auto" }}>
                      <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#eb001b" }} />
                      <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#f79e1b", marginLeft: -8 }} />
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-white p-3">
                <ul className="text-xs text-zinc-600 space-y-1 mb-3">
                  {pt.features.map((f, i) => <li key={i} className="flex items-start gap-1"><span className="text-green-500 mt-0.5">✓</span>{f}</li>)}
                </ul>
                <Button size="sm" variant="outline" className="w-full" onClick={() => { setEditPhysType(pt); setPhysEditForm({ name: pt.name, price: pt.price, features: pt.features.join("\n") }); }}>
                  Modifier
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dialog — modifier un type de carte physique */}
      <Dialog open={!!editPhysType} onOpenChange={(o) => { if (!o) setEditPhysType(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Carte {editPhysType?.name} — Modifier</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nom affiché</Label>
              <Input value={physEditForm.name} onChange={(e) => setPhysEditForm({ ...physEditForm, name: e.target.value })} placeholder="Metal, Premium, Gold…" />
            </div>
            <div>
              <Label>Prix (€)</Label>
              <Input type="number" min={0} step={0.01} value={physEditForm.price} onChange={(e) => setPhysEditForm({ ...physEditForm, price: e.target.value })} />
            </div>
            <div>
              <Label>Avantages (un par ligne)</Label>
              <textarea
                className="w-full border border-zinc-200 rounded-lg p-2 text-sm text-zinc-800 resize-none focus:outline-none focus:ring-2 focus:ring-zinc-300"
                rows={6}
                value={physEditForm.features}
                onChange={(e) => setPhysEditForm({ ...physEditForm, features: e.target.value })}
                placeholder={"Cashback 2%\nAssurance voyage\n..."}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPhysType(null)}>Annuler</Button>
            <Button className="bg-zinc-900 text-white" onClick={savePhysType} disabled={physLoading}>{physLoading ? "Enregistrement…" : "Enregistrer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog — modifier la carte */}
      <Dialog open={!!editCard} onOpenChange={(o) => { if (!o) setEditCard(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Modifier la carte — {editCard?.holder_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Titulaire</Label><Input value={editForm.holder_name || ""} onChange={(e) => setEditForm({ ...editForm, holder_name: e.target.value.toUpperCase() })} /></div>
            <div><Label>Numéro complet</Label><Input value={editForm.number_full || ""} onChange={(e) => setEditForm({ ...editForm, number_full: e.target.value })} placeholder="4242 1234 5678 9012" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>CVV</Label><Input value={editForm.cvv || ""} onChange={(e) => setEditForm({ ...editForm, cvv: e.target.value })} placeholder="123" maxLength={4} /></div>
              <div><Label>Expiration</Label><Input value={editForm.expiry || ""} onChange={(e) => setEditForm({ ...editForm, expiry: e.target.value })} placeholder="MM/AA" maxLength={5} /></div>
            </div>
            <div>
              <Label>Réseau</Label>
              <Select value={editForm.card_type || "visa"} onValueChange={(v) => setEditForm({ ...editForm, card_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="visa">VISA</SelectItem>
                  <SelectItem value="mastercard">Mastercard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="border-t border-zinc-100 pt-3">
              <Label className="text-xs text-zinc-500 uppercase tracking-wide">Plafonds de dépense</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <div><Label className="text-xs">Journalier (€)</Label><Input type="number" min={0} value={editForm.limit_daily ?? 1000} onChange={(e) => setEditForm({ ...editForm, limit_daily: Number(e.target.value) })} /></div>
                <div><Label className="text-xs">Hebdomadaire (€)</Label><Input type="number" min={0} value={editForm.limit_weekly ?? 5000} onChange={(e) => setEditForm({ ...editForm, limit_weekly: Number(e.target.value) })} /></div>
                <div><Label className="text-xs">Mensuel (€)</Label><Input type="number" min={0} value={editForm.limit_monthly ?? 10000} onChange={(e) => setEditForm({ ...editForm, limit_monthly: Number(e.target.value) })} /></div>
              </div>
            </div>
          </div>
          <div className="md:col-span-2 flex items-center gap-3 pt-2">
            <Switch
              checked={!!editForm.card_confirmed}
              onCheckedChange={(v) => setEditForm({ ...editForm, card_confirmed: v })}
              data-testid="card-confirmed"
            />
            <label className="text-sm font-medium">Carte confirmée (révèle les 4 derniers chiffres)</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCard(null)}>Annuler</Button>
            <Button className="bg-zinc-900 text-white" onClick={saveEdit} disabled={editLoading}>{editLoading ? "Enregistrement…" : "Enregistrer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={resetConfirm} title="Réinitialiser les cartes physiques" message="Remplacer la configuration actuelle par les 3 types par défaut (Metal 29€, Premium 49€, Gold 99€) ? Tes personnalisations seront perdues." onOk={resetPhysTypes} onCancel={() => setResetConfirm(false)} />
      <ConfirmDialog open={!!deleteTarget} title="Supprimer la carte" message={`Supprimer définitivement la carte de ${deleteTarget?.holder_name || "ce client"} (${deleteTarget?.number_masked || ""}) ? Cette action est irréversible.`} onOk={deleteCard} onCancel={() => setDeleteTarget(null)} />
      <ConfirmDialog open={!!physOrderDeleteTarget} danger={false} title="Supprimer la commande physique" message={`Supprimer la commande physique de ${physOrderDeleteTarget?.holder_name || "ce client"} ? La carte virtuelle et ses informations seront conservées.`} onOk={deletePhysicalOrder} onCancel={() => setPhysOrderDeleteTarget(null)} />
    </div>
  );
}

function BankingOps({ bankId, users, reload }) {
  const [form, setForm] = useState({ bank_user_id: "", op: "recharge", amount: 0, label: "" });
  const [resetAllConfirm, setResetAllConfirm] = useState(false);
  const [resetUserConfirm, setResetUserConfirm] = useState(false);
  const selectedUser = users.find((u) => u.id === form.bank_user_id);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.bank_user_id) return toast.error("Sélectionne un utilisateur");
    try { await api.post(`/banks/${bankId}/banking-ops`, { ...form, amount: Number(form.amount) }); toast.success("Opération enregistrée"); setForm({ bank_user_id: "", op: "recharge", amount: 0, label: "" }); reload(); }
    catch (err) { toast.error(formatError(err)); }
  };
  const resetAll = async () => {
    try { await api.post(`/banks/${bankId}/reset-history`); toast.success("Historique global réinitialisé"); reload(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const resetUser = async () => {
    if (!selectedUser) return;
    try { await api.post(`/banks/${bankId}/users/${selectedUser.id}/reset-history`); toast.success(`Historique de ${selectedUser.name} réinitialisé`); reload(); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div>
      <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2"><WalletIcon className="w-5 h-5" /> Opérations bancaires</h2>
      <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-xl p-5 grid md:grid-cols-2 lg:grid-cols-5 gap-3 items-end mb-4">
        <div>
          <Label>Utilisateur</Label>
          <Select value={form.bank_user_id} onValueChange={(v) => setForm({ ...form, bank_user_id: v })}>
            <SelectTrigger data-testid="op-user"><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} — {fmtMoney(u.balance, u.currency || "EUR")} ({u.currency || "EUR"})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Opération</Label>
          <Select value={form.op} onValueChange={(v) => setForm({ ...form, op: v })}>
            <SelectTrigger data-testid="op-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recharge">Recharger (+)</SelectItem>
              <SelectItem value="refund">Remboursement (+)</SelectItem>
              <SelectItem value="transfer_in">Virement entrant (+)</SelectItem>
              <SelectItem value="withdraw">Retrait (-)</SelectItem>
              <SelectItem value="transfer_out">Virement sortant (-)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Montant {selectedUser ? <span className="text-zinc-400 font-normal">({selectedUser.currency || "EUR"})</span> : ""}</Label>
          <Input type="number" min={0} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="op-amount" placeholder={selectedUser ? `en ${selectedUser.currency || "EUR"}` : "0"} />
        </div>
        <div><Label>Libellé (optionnel)</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="ex: virement BNP Paribas" data-testid="op-label" /></div>
        <Button type="submit" className="bg-zinc-900 hover:bg-zinc-700 text-white" data-testid="op-submit">Valider l'opération</Button>
      </form>

      {selectedUser && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-900 flex items-center justify-between mb-3">
          <div>Réinitialiser uniquement l'historique et le solde de <strong>{selectedUser.name}</strong>.</div>
          <Button variant="outline" onClick={() => setResetUserConfirm(true)} className="text-red-600 border-red-300 hover:bg-red-50">Réinitialiser {selectedUser.name}</Button>
          <ConfirmDialog open={resetUserConfirm} title={`Réinitialiser ${selectedUser.name}`} message={`Supprimer toutes les transactions et remettre le solde de ${selectedUser.name} à zéro ? Cette action est irréversible.`} onOk={() => { setResetUserConfirm(false); resetUser(); }} onCancel={() => setResetUserConfirm(false)} />
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 flex items-center justify-between">
        <div>⚠️ Réinitialiser l'historique de <strong>tous les utilisateurs</strong> — remet tous les soldes à zéro.</div>
        <Button variant="outline" onClick={() => setResetAllConfirm(true)} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="op-reset">Réinitialiser tout</Button>
        <ConfirmDialog open={resetAllConfirm} title="Réinitialiser tout l'historique" message="Réinitialiser tout l'historique des transactions et les soldes de TOUS les utilisateurs ? Cette action est irréversible." onOk={() => { setResetAllConfirm(false); resetAll(); }} onCancel={() => setResetAllConfirm(false)} />
      </div>
    </div>
  );
}

function BankNotifications({ bankId, users }) {
  const [notifs, setNotifs] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", bank_user_id: "all" });
  const load = () => api.get(`/banks/${bankId}/notifications`).then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/banks/${bankId}/notifications`, {
        title: form.title, body: form.body,
        bank_user_id: form.bank_user_id === "all" ? null : form.bank_user_id,
      });
      toast.success("Notification envoyée"); setForm({ title: "", body: "", bank_user_id: "all" }); load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const del = async (nid) => {
    try { await api.delete(`/banks/${bankId}/notifications/${nid}`); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div>
      <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2"><Bell className="w-5 h-5" /> Notifications dashboard client</h2>
      <form onSubmit={send} className="bg-white border border-zinc-200 rounded-xl p-5 grid md:grid-cols-2 gap-3 mb-4">
        <div className="md:col-span-2">
          <Label>Destinataire</Label>
          <Select value={form.bank_user_id} onValueChange={(v) => setForm({ ...form, bank_user_id: v })}>
            <SelectTrigger data-testid="notif-target"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les utilisateurs (broadcast)</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2"><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="notif-title" /></div>
        <div className="md:col-span-2"><Label>Message</Label><Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={3} required data-testid="notif-body" /></div>
        <Button type="submit" className="md:col-span-2 bg-zinc-900 hover:bg-zinc-700 text-white" data-testid="notif-send">Envoyer</Button>
      </form>
      <div className="space-y-2">
        {notifs.map((n) => (
          <div key={n.id} className="bg-white border border-zinc-200 rounded-xl p-4 flex items-start justify-between gap-3">
            <div>
              <div className="font-semibold">{n.title}</div>
              <div className="text-sm text-zinc-500">{n.body}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-1">
                {n.bank_user_id ? "Privée" : "Broadcast"} · {new Date(n.created_at).toLocaleString("fr-FR")}
              </div>
            </div>
            <Button size="icon" variant="ghost" onClick={() => del(n.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        {!notifs.length && <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-zinc-400">Aucune notification.</div>}
      </div>
    </div>
  );
}

function PendingTransfers({ bankId }) {
  const [items, setItems] = useState([]);
  const load = () => api.get(`/banks/${bankId}/transfers`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const viewCode = async (tid) => {
    try { const { data } = await api.get(`/banks/${bankId}/transfers/${tid}/code`); alert(`Code de validation : ${data.code}`); }
    catch (e) { toast.error(formatError(e)); }
  };
  const sendCode = async (tid) => {
    try { await api.post(`/banks/${bankId}/transfers/${tid}/send-code`); toast.success("Code envoyé au client (expire dans 5 min)"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const reject = async (tid) => {
    try { await api.post(`/banks/${bankId}/transfers/${tid}/reject`); toast.success("Virement rejeté"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div>
      <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2"><SendIcon className="w-5 h-5" /> Virements clients</h2>
      <div className="space-y-2">
        {items.map((t) => (
          <div key={t.id} className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`transfer-${t.id}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{t.bank_user_name}</span>
                <Badge className={
                  t.status === "validated" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                  t.status === "rejected" ? "bg-red-100 text-red-700 border-red-200" :
                  "bg-amber-100 text-amber-700 border-amber-200"
                }>{t.status}</Badge>
              </div>
              <div className="text-sm text-zinc-500 mt-1">{t.label} → {t.recipient}</div>
            </div>
            <div className="font-mono-num font-bold">{fmtMoney(t.amount)}</div>
            {t.status === "pending" && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => viewCode(t.id)} data-testid={`transfer-viewcode-${t.id}`}>Voir le code</Button>
                <Button size="sm" onClick={() => sendCode(t.id)} className="bg-zinc-900 hover:bg-zinc-700 text-white" data-testid={`transfer-code-${t.id}`}>Envoyer le code</Button>
                <Button size="sm" variant="outline" onClick={() => reject(t.id)} className="text-red-600" data-testid={`transfer-reject-${t.id}`}>Rejeter</Button>
              </div>
            )}
          </div>
        ))}
        {!items.length && <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-zinc-400">Aucun virement en attente.</div>}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* MESSAGING — Messenger-style admin module                                   */
/* -------------------------------------------------------------------------- */
const MSG_CSS = `
.am-wrap {
  display: flex;
  height: 70vh;
  min-height: 480px;
  background: #fff;
  border: 1px solid #e4e4e7;
  border-radius: 14px;
  overflow: hidden;
}
.am-sidebar {
  width: 320px;
  border-right: 1px solid #e4e4e7;
  display: flex;
  flex-direction: column;
  background: #fafafa;
  flex-shrink: 0;
}
.am-sidebar-head { padding: 14px 16px; border-bottom: 1px solid #e4e4e7; }
.am-search-input {
  width: 100%; padding: 8px 12px;
  border: 1px solid #e4e4e7; border-radius: 8px;
  font-size: 0.85rem; outline: none;
  background: #fff;
}
.am-conv-list { flex: 1; overflow-y: auto; }
.am-conv-empty { text-align: center; color: #a1a1aa; padding: 30px 20px; font-size: 0.85rem; }
.am-conv-item {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 16px; cursor: pointer;
  border-bottom: 1px solid #f4f4f5;
  transition: background 0.12s;
}
.am-conv-item:hover { background: #f4f4f5; }
.am-conv-item.active { background: #fff; border-left: 3px solid #2563eb; padding-left: 13px; }
.am-conv-avatar {
  width: 38px; height: 38px; border-radius: 50%;
  background: #2563eb; color: #fff;
  display: grid; place-items: center;
  font-weight: 700; font-size: 0.78rem; flex-shrink: 0;
}
.am-conv-info { flex: 1; min-width: 0; }
.am-conv-name { font-weight: 600; font-size: 0.85rem; color: #18181b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.am-conv-last { font-size: 0.72rem; color: #71717a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
.am-conv-unread {
  background: #ef4444; color: #fff;
  min-width: 18px; height: 18px; border-radius: 9px;
  font-size: 0.65rem; font-weight: 700;
  display: grid; place-items: center; padding: 0 5px;
  flex-shrink: 0;
}
.am-chat-area { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.am-chat-empty {
  flex: 1; display: flex; align-items: center; justify-content: center;
  color: #a1a1aa; font-size: 0.9rem; flex-direction: column; gap: 12px;
}
.am-chat-head {
  padding: 12px 18px; border-bottom: 1px solid #e4e4e7;
  display: flex; align-items: center; gap: 10px;
  background: #fff; flex-shrink: 0;
}
.am-chat-body {
  flex: 1; overflow-y: auto; padding: 16px 20px;
  display: flex; flex-direction: column; gap: 8px;
  background: #fafafa;
}
.am-bubble {
  max-width: 70%; padding: 8px 14px; border-radius: 16px;
  font-size: 0.85rem; line-height: 1.4;
  word-wrap: break-word; white-space: pre-wrap;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}
.am-bubble.admin {
  align-self: flex-end; background: #2563eb; color: #fff;
  border-bottom-right-radius: 4px;
}
.am-bubble.user {
  align-self: flex-start; background: #fff; color: #18181b;
  border: 1px solid #e4e4e7; border-bottom-left-radius: 4px;
}
.am-bubble.system {
  align-self: center; background: #fef3c7; color: #92400e;
  font-size: 0.75rem; max-width: 80%; text-align: center; border-radius: 10px;
}
.am-bubble-time { font-size: 0.6rem; opacity: 0.65; margin-top: 3px; }
.am-att-img { display: block; max-width: 240px; max-height: 220px; border-radius: 10px; margin-bottom: 6px; cursor: pointer; object-fit: cover; }
.am-att-audio { display: block; max-width: 240px; margin-bottom: 6px; }
.am-att-file { display: flex; align-items: center; gap: 10px; padding: 8px 10px; background: rgba(0,0,0,0.06); border-radius: 10px; margin-bottom: 6px; text-decoration: none; color: inherit; max-width: 240px; }
.am-bubble.admin .am-att-file { background: rgba(255,255,255,0.18); }
.am-att-file-info { flex: 1; min-width: 0; }
.am-att-file-name { font-size: 0.78rem; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.am-att-file-meta { font-size: 0.65rem; opacity: 0.7; }

.am-chat-form {
  display: flex; gap: 8px; padding: 12px 16px; align-items: center; position: relative;
  border-top: 1px solid #e4e4e7; background: #fff;
}
.am-attach-btn {
  width: 36px; height: 36px; border-radius: 50%; border: 1px solid #e4e4e7;
  background: #fafafa; color: #71717a; cursor: pointer;
  display: grid; place-items: center; transition: background 0.12s;
  flex-shrink: 0;
}
.am-attach-btn:hover { background: #2563eb; color: #fff; border-color: #2563eb; }
.am-attach-menu {
  position: absolute; bottom: 60px; left: 14px;
  background: #fff; border: 1px solid #e4e4e7; border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12); padding: 6px;
  display: flex; flex-direction: column; gap: 2px; min-width: 180px; z-index: 10;
}
.am-attach-item {
  display: flex; align-items: center; gap: 10px; padding: 9px 12px;
  border-radius: 10px; border: none; background: transparent;
  font-size: 0.82rem; color: #18181b; cursor: pointer; text-align: left;
}
.am-attach-item:hover { background: #f4f4f5; }
.am-attach-preview {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 10px; background: #fafafa; border: 1px solid #e4e4e7;
  border-radius: 10px; margin: 0 16px 8px;
  font-size: 0.76rem; color: #18181b;
}
.am-attach-preview-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.am-attach-preview button { background: none; border: none; cursor: pointer; color: #71717a; display: grid; place-items: center; }

.am-chat-input {
  flex: 1; padding: 9px 14px; border-radius: 20px;
  border: 1px solid #e4e4e7; font-size: 0.88rem;
  outline: none;
}
.am-chat-input:focus { border-color: #2563eb; }
.am-chat-send {
  width: 38px; height: 38px; border-radius: 50%;
  background: #2563eb; color: #fff; border: none; cursor: pointer;
  display: grid; place-items: center;
}
.am-chat-send:disabled { opacity: 0.4; cursor: not-allowed; }

@media (max-width: 768px) {
  .am-wrap { flex-direction: column; height: auto; }
  .am-sidebar { width: 100%; max-height: 280px; }
}
`;

const ADMIN_MAX_ATTACH_BYTES = 8 * 1024 * 1024;

function adminDetectKind(mime, name = "") {
  const m = (mime || "").toLowerCase();
  const n = (name || "").toLowerCase();
  if (m.startsWith("image/")) return "image";
  if (m.startsWith("audio/")) return "audio";
  if (m === "application/pdf" || n.endsWith(".pdf")) return "pdf";
  return "file";
}
function adminFileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
function adminFmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function AdminMessageBubble({ m }) {
  const att = m.attachment_url;
  const kind = m.attachment_kind || (att ? adminDetectKind(m.attachment_type, m.attachment_name) : null);
  return (
    <div className={`am-bubble ${m.from_role}`}>
      {att && kind === "image" && (
        <img src={att} alt={m.attachment_name || "image"} className="am-att-img" onClick={() => window.open(att, "_blank")} />
      )}
      {att && kind === "audio" && (
        <audio controls src={att} className="am-att-audio" />
      )}
      {att && (kind === "pdf" || kind === "file") && (
        <a className="am-att-file" href={att} download={m.attachment_name || "fichier"} target="_blank" rel="noreferrer">
          {kind === "pdf" ? <FileText size={22} /> : <FileIcon size={22} />}
          <div className="am-att-file-info">
            <div className="am-att-file-name">{m.attachment_name || (kind === "pdf" ? "Document PDF" : "Fichier")}</div>
            <div className="am-att-file-meta">{kind === "pdf" ? "PDF" : (m.attachment_type || "Fichier")}</div>
          </div>
          <Download size={16} style={{ opacity: 0.7 }} />
        </a>
      )}
      {m.text && <div>{m.text}</div>}
      <div className="am-bubble-time">{new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
    </div>
  );
}

function BankMessages({ bankId, users = [], onRead }) {
  const [convs, setConvs] = useState([]);
  const [search, setSearch] = useState("");
  const [newConvOpen, setNewConvOpen] = useState(false);
  const [newConvSearch, setNewConvSearch] = useState("");
  const [activeUserId, setActiveUserId] = useState(null);
  const [chat, setChat] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [pending, setPending] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const imgInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchConvs = async () => {
    try {
      const { data } = await api.get(`/banks/${bankId}/conversations`);
      setConvs(data);
    } catch (_) { setConvs([]); }
  };

  const fetchChat = async (uid) => {
    try {
      const { data } = await api.get(`/banks/${bankId}/conversations/${uid}`);
      setChat(data);
      fetchConvs();
      onRead?.(); // opening a conversation marks its messages read — refresh the badge
    } catch (_) { setChat(null); }
  };

  useEffect(() => {
    fetchConvs();
    const t = setInterval(fetchConvs, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankId]);

  useEffect(() => {
    if (!activeUserId) return;
    fetchChat(activeUserId);
    const t = setInterval(() => fetchChat(activeUserId), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUserId]);

  const pickFile = async (file) => {
    if (!file) return;
    if (file.size > ADMIN_MAX_ATTACH_BYTES) {
      toast.error(`Fichier trop volumineux (max ${adminFmtSize(ADMIN_MAX_ATTACH_BYTES)}).`);
      return;
    }
    const dataUrl = await adminFileToDataUrl(file);
    setPending({ file, dataUrl, kind: adminDetectKind(file.type, file.name) });
    setMenuOpen(false);
  };

  const send = async (e) => {
    e.preventDefault();
    if ((!text.trim() && !pending) || !activeUserId) return;
    setSending(true);
    try {
      const payload = { text: text.trim() };
      if (pending) {
        payload.attachment_url = pending.dataUrl;
        payload.attachment_name = pending.file.name;
        payload.attachment_type = pending.file.type || "application/octet-stream";
        payload.attachment_kind = pending.kind;
      }
      await api.post(`/banks/${bankId}/conversations/${activeUserId}`, payload);
      setText("");
      setPending(null);
      fetchChat(activeUserId);
    } catch (err) { toast.error(formatError(err)); }
    finally { setSending(false); }
  };

  const clearHistory = async () => {
    if (!activeUserId || !chat) return;
    try {
      await api.delete(`/banks/${bankId}/conversations/${activeUserId}`);
      toast.success("Historique effacé");
      fetchChat(activeUserId);
      fetchConvs();
    } catch (err) { toast.error(formatError(err)); }
  };

  const startConv = (u) => {
    setNewConvOpen(false);
    setNewConvSearch("");
    setActiveUserId(u.id);
  };

  const filtered = convs.filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.email.toLowerCase().includes(search.toLowerCase()));

  const convUserIds = new Set(convs.map((c) => c.bank_user_id));
  const usersWithoutConv = users.filter((u) => !convUserIds.has(u.id) && (
    !newConvSearch || u.name.toLowerCase().includes(newConvSearch.toLowerCase()) || u.email.toLowerCase().includes(newConvSearch.toLowerCase())
  ));

  const initials = (name) => (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div>
      <style>{MSG_CSS}</style>
      <div className="am-wrap">
        <div className="am-sidebar">
          <div className="am-sidebar-head" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <input className="am-search-input" placeholder="Rechercher une conversation…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {users.length > 0 && (
              <button type="button" onClick={() => setNewConvOpen((v) => !v)} style={{ background: "#18181b", color: "#fff", border: "none", borderRadius: 8, padding: "7px 10px", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                <PlusCircle size={13} /> Nouvelle conversation
              </button>
            )}
          </div>
          {newConvOpen && (
            <div style={{ padding: "8px 10px", borderBottom: "1px solid #f4f4f5" }}>
              <input className="am-search-input" placeholder="Rechercher un client…" value={newConvSearch} onChange={(e) => setNewConvSearch(e.target.value)} autoFocus />
              <div style={{ maxHeight: 180, overflowY: "auto", marginTop: 6 }}>
                {usersWithoutConv.length === 0 ? (
                  <div style={{ fontSize: "0.75rem", color: "#a1a1aa", padding: "8px 4px" }}>Tous les clients ont déjà une conversation.</div>
                ) : usersWithoutConv.map((u) => (
                  <div key={u.id} onClick={() => startConv(u)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 6px", borderRadius: 8, cursor: "pointer", fontSize: "0.82rem" }} className="am-conv-item">
                    <div className="am-conv-avatar" style={{ width: 28, height: 28, fontSize: "0.7rem" }}>{initials(u.name)}</div>
                    <div><div style={{ fontWeight: 600 }}>{u.name}</div><div style={{ fontSize: "0.7rem", color: "#71717a" }}>{u.email}</div></div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="am-conv-list">
            {filtered.length === 0 ? (
              <div className="am-conv-empty">{convs.length === 0 ? "Aucune conversation pour l'instant." : "Aucun résultat."}</div>
            ) : (
              filtered.map((c) => (
                <div key={c.bank_user_id} className={`am-conv-item${activeUserId === c.bank_user_id ? " active" : ""}`} onClick={() => setActiveUserId(c.bank_user_id)} data-testid={`admin-conv-${c.bank_user_id}`}>
                  <div className="am-conv-avatar">{initials(c.name)}</div>
                  <div className="am-conv-info">
                    <div className="am-conv-name">{c.name}</div>
                    <div className="am-conv-last">{c.last_from_role === "admin" ? "Vous : " : c.last_from_role === "system" ? "📌 " : ""}{c.last_text}</div>
                  </div>
                  {c.unread > 0 && <div className="am-conv-unread">{c.unread}</div>}
                </div>
              ))
            )}
          </div>
        </div>
        <div className="am-chat-area">
          {!activeUserId ? (
            <div className="am-chat-empty">
              <MessageCircle size={48} style={{ opacity: 0.3 }} />
              <span>Sélectionnez une conversation pour commencer</span>
            </div>
          ) : !chat ? (
            <div className="am-chat-empty"><span>Chargement…</span></div>
          ) : (
            <>
              <div className="am-chat-head">
                <div className="am-conv-avatar">{initials(chat.user?.name)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.92rem" }}>{chat.user?.name}</div>
                  <div style={{ fontSize: "0.72rem", color: "#71717a" }}>{chat.user?.email}</div>
                </div>
                {chat.messages?.length > 0 && (
                  <>
                  <button
                    type="button"
                    onClick={() => setClearConfirm(true)}
                    title="Effacer l'historique de cette conversation"
                    data-testid="admin-msg-clear-history"
                    style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", background: "transparent", border: "1px solid #fecaca", color: "#dc2626", borderRadius: 8, cursor: "pointer", fontSize: "0.78rem", fontWeight: 600 }}
                  >
                    <Trash2 size={13} />Effacer l'historique
                  </button>
                  <ConfirmDialog open={clearConfirm} title="Effacer l'historique" message={`Effacer définitivement les ${chat?.messages?.length || 0} message${(chat?.messages?.length || 0) > 1 ? "s" : ""} échangés avec ${chat?.user?.name || "cet utilisateur"} ? Cette action est irréversible.`} onOk={() => { setClearConfirm(false); clearHistory(); }} onCancel={() => setClearConfirm(false)} />
                  </>
                )}
              </div>
              <div className="am-chat-body" ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}>
                {chat.messages.length === 0 ? (
                  <div className="am-chat-empty">Aucun message dans cette conversation.</div>
                ) : (
                  chat.messages.map((m) => <AdminMessageBubble key={m.id} m={m} />)
                )}
              </div>

              {pending && (
                <div className="am-attach-preview" data-testid="admin-msg-attach-preview">
                  {pending.kind === "image" ? <ImageIcon size={16} /> : pending.kind === "audio" ? <Mic size={16} /> : pending.kind === "pdf" ? <FileText size={16} /> : <FileIcon size={16} />}
                  <span className="am-attach-preview-name">{pending.file.name} <span style={{ opacity: 0.6 }}>({adminFmtSize(pending.file.size)})</span></span>
                  <button type="button" onClick={() => setPending(null)} aria-label="Retirer"><X size={14} /></button>
                </div>
              )}

              <form className="am-chat-form" onSubmit={send}>
                {menuOpen && (
                  <div className="am-attach-menu" onMouseLeave={() => setMenuOpen(false)}>
                    <button type="button" className="am-attach-item" onClick={() => imgInputRef.current?.click()}><ImageIcon size={16} color="#22c55e" />Image</button>
                    <button type="button" className="am-attach-item" onClick={() => pdfInputRef.current?.click()}><FileText size={16} color="#ef4444" />PDF</button>
                    <button type="button" className="am-attach-item" onClick={() => audioInputRef.current?.click()}><Mic size={16} color="#8b5cf6" />Audio</button>
                    <button type="button" className="am-attach-item" onClick={() => fileInputRef.current?.click()}><FileIcon size={16} color="#0ea5e9" />Fichier / Document</button>
                  </div>
                )}
                <input ref={imgInputRef} type="file" accept="image/*" hidden onChange={(e) => pickFile(e.target.files?.[0])} />
                <input ref={pdfInputRef} type="file" accept="application/pdf,.pdf" hidden onChange={(e) => pickFile(e.target.files?.[0])} />
                <input ref={audioInputRef} type="file" accept="audio/*" hidden onChange={(e) => pickFile(e.target.files?.[0])} />
                <input ref={fileInputRef} type="file" hidden onChange={(e) => pickFile(e.target.files?.[0])} />

                <button type="button" className="am-attach-btn" onClick={() => setMenuOpen((v) => !v)} data-testid="admin-msg-attach-btn" aria-label="Joindre un fichier">
                  <Paperclip size={15} />
                </button>
                <input className="am-chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Écrire un message…" data-testid="admin-msg-input" />
                <button type="submit" className="am-chat-send" disabled={sending || (!text.trim() && !pending)} data-testid="admin-msg-send">
                  <SendIcon size={15} />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* Small row of key/value lines for the "Voir les modalités" dialogs */
function DetailRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-zinc-100 last:border-0">
      <span className="text-xs text-zinc-500">{label}</span>
      <span className="text-sm font-medium text-zinc-800 text-right">{value}</span>
    </div>
  );
}

function StatusBadge({ status, labels }) {
  return (
    <Badge variant={status === "approved" ? "default" : status === "rejected" ? "destructive" : "secondary"}>
      {status === "pending" ? "En attente" : status === "approved" ? labels.ok : labels.no}
    </Badge>
  );
}

/* ── Sub-tab toggle for catalog vs requests ─────────────────────────────── */
function CatalogToggle({ view, setView, catalogLabel, requestsLabel }) {
  return (
    <div className="inline-flex bg-zinc-100 rounded-lg p-1 mb-4">
      <button
        onClick={() => setView("catalog")}
        className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${view === "catalog" ? "bg-white shadow text-zinc-900" : "text-zinc-500"}`}
        data-testid="toggle-catalog"
      >{catalogLabel}</button>
      <button
        onClick={() => setView("requests")}
        className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${view === "requests" ? "bg-white shadow text-zinc-900" : "text-zinc-500"}`}
        data-testid="toggle-requests"
      >{requestsLabel}</button>
    </div>
  );
}

function BankCreditsTab({ bankId }) {
  const [view, setView] = useState("catalog");
  return (
    <div>
      <CatalogToggle view={view} setView={setView} catalogLabel="Offres de crédit" requestsLabel="Demandes des clients" />
      {view === "catalog" ? <CreditProductsManager bankId={bankId} /> : <BankCreditRequests bankId={bankId} />}
    </div>
  );
}

function BankInvestmentsTab({ bankId }) {
  const [view, setView] = useState("catalog");
  return (
    <div>
      <CatalogToggle view={view} setView={setView} catalogLabel="Offres d'investissement" requestsLabel="Demandes des clients" />
      {view === "catalog" ? <InvestmentProductsManager bankId={bankId} /> : <BankInvestmentRequests bankId={bankId} />}
    </div>
  );
}

function BankFinancingsTab({ bankId }) {
  const [view, setView] = useState("catalog");
  return (
    <div>
      <CatalogToggle view={view} setView={setView} catalogLabel="Offres de financement" requestsLabel="Demandes des clients" />
      {view === "catalog" ? <FinancingProductsManager bankId={bankId} /> : <BankFinancingRequests bankId={bankId} />}
    </div>
  );
}

const PRODUCT_COLORS = ["#2563eb", "#0ea5e9", "#06b6d4", "#10b981", "#16a34a", "#65a30d", "#f59e0b", "#f97316", "#ef4444", "#ec4899", "#8b5cf6", "#6366f1", "#d4af37", "#64748b"];

function ColorPicker({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {PRODUCT_COLORS.map((c) => (
        <button key={c} type="button" onClick={() => onChange(c)}
          className={`w-7 h-7 rounded-full border-2 ${value === c ? "border-zinc-900" : "border-transparent"}`}
          style={{ background: c }} aria-label={c} />
      ))}
    </div>
  );
}

/* ── Credit products catalog ─────────────────────────────────────────────── */
const EMPTY_CREDIT_PRODUCT = { name: "", description: "", color: "#2563eb", amount_min: 1000, amount_max: 50000, rate_min: 2, rate_max: 4, duration_min: 1, duration_max: 120, deposit: 0, garantie: "Aucune", eligibility: "", active: true };

function CreditProductsManager({ bankId }) {
  const [items, setItems] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_CREDIT_PRODUCT);
  const [delConfirm, setDelConfirm] = useState(null);
  const load = async () => {
    try { const { data } = await api.get(`/banks/${bankId}/credit-products`); setItems(data); }
    catch { setItems([]); }
  };
  useEffect(() => { load(); }, [bankId]);

  const openNew = () => { setEditItem("new"); setForm(EMPTY_CREDIT_PRODUCT); };
  const openEdit = (p) => { setEditItem(p); setForm({ ...EMPTY_CREDIT_PRODUCT, ...p }); };
  const save = async () => {
    const payload = {
      name: form.name, description: form.description, color: form.color,
      amount_min: Number(form.amount_min), amount_max: Number(form.amount_max),
      rate_min: Number(form.rate_min), rate_max: Number(form.rate_max),
      duration_min: Number(form.duration_min), duration_max: Number(form.duration_max),
      deposit: Number(form.deposit), garantie: form.garantie, eligibility: form.eligibility, active: form.active,
    };
    try {
      if (editItem === "new") await api.post(`/banks/${bankId}/credit-products`, payload);
      else await api.patch(`/banks/${bankId}/credit-products/${editItem.id}`, payload);
      toast.success("Offre enregistrée"); setEditItem(null); load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const remove = async (p) => {
    try { await api.delete(`/banks/${bankId}/credit-products/${p.id}`); toast.success("Offre supprimée"); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold">Catalogue de crédits ({items.length})</h2>
        <Button size="sm" className="bg-zinc-900 text-white" onClick={openNew} data-testid="credit-product-new"><Plus className="w-4 h-4 mr-1" />Nouvelle offre</Button>
      </div>
      <p className="text-xs text-zinc-400">Personnalisez les offres de crédit affichées aux clients. Ces modifications sont visibles immédiatement côté utilisateur.</p>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-zinc-200 rounded-xl p-4" style={{ borderLeft: `4px solid ${p.color}` }}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm flex items-center gap-2">{p.name}{p.active === false && <span className="text-xs bg-zinc-200 text-zinc-500 px-2 py-0.5 rounded-full">Masqué</span>}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{p.description}</div>
              </div>
            </div>
            <div className="text-xs text-zinc-600 mt-2 space-y-0.5">
              <div>Montant : {Number(p.amount_min).toLocaleString()}€ – {Number(p.amount_max).toLocaleString()}€</div>
              <div>Taux : {p.rate_min}% – {p.rate_max}% · Durée : {p.duration_min}–{p.duration_max} mois</div>
              <div>Dépôt : {Number(p.deposit).toLocaleString()}€ · Garantie : {p.garantie}</div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" className="text-xs" onClick={() => openEdit(p)}><Pencil className="w-3.5 h-3.5 mr-1" />Modifier</Button>
              <Button size="sm" variant="outline" className="text-xs text-red-600" onClick={() => setDelConfirm(p)}><Trash2 className="w-3.5 h-3.5 mr-1" />Supprimer</Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-zinc-400 md:col-span-2">Aucune offre de crédit.</div>}
      </div>

      <Dialog open={!!editItem} onOpenChange={(o) => { if (!o) setEditItem(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editItem === "new" ? "Nouvelle offre de crédit" : "Modifier l'offre"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Nom du crédit *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Crédit Personnel" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Montant min (€)</Label><Input type="number" value={form.amount_min} onChange={(e) => setForm({ ...form, amount_min: e.target.value })} /></div>
              <div><Label>Montant max (€)</Label><Input type="number" value={form.amount_max} onChange={(e) => setForm({ ...form, amount_max: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Taux min (%)</Label><Input type="number" step={0.1} value={form.rate_min} onChange={(e) => setForm({ ...form, rate_min: e.target.value })} /></div>
              <div><Label>Taux max (%)</Label><Input type="number" step={0.1} value={form.rate_max} onChange={(e) => setForm({ ...form, rate_max: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Durée min (mois)</Label><Input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} /></div>
              <div><Label>Durée max (mois)</Label><Input type="number" value={form.duration_max} onChange={(e) => setForm({ ...form, duration_max: e.target.value })} /></div>
            </div>
            <div><Label>Dépôt initial (€)</Label><Input type="number" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} /></div>
            <div><Label>Garantie demandée</Label><Input value={form.garantie} onChange={(e) => setForm({ ...form, garantie: e.target.value })} placeholder="Aucune, Hypothèque…" /></div>
            <div><Label>Conditions d'éligibilité</Label><Textarea rows={2} value={form.eligibility} onChange={(e) => setForm({ ...form, eligibility: e.target.value })} placeholder="Justifier de revenus réguliers…" /></div>
            <div><Label>Couleur</Label><ColorPicker value={form.color} onChange={(c) => setForm({ ...form, color: c })} /></div>
            <div className="flex items-center gap-3"><Switch checked={form.active !== false} onCheckedChange={(v) => setForm({ ...form, active: v })} /><label className="text-sm">Visible par les clients</label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>Annuler</Button>
            <Button className="bg-zinc-900 text-white" onClick={save} disabled={!form.name}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!delConfirm} title="Supprimer l'offre" message={`Supprimer définitivement l'offre « ${delConfirm?.name} » ?`} onOk={() => { remove(delConfirm); setDelConfirm(null); }} onCancel={() => setDelConfirm(null)} />
    </div>
  );
}

/* ── Investment products catalog ─────────────────────────────────────────── */
const EMPTY_INVEST_PRODUCT = { name: "", description: "", color: "#0ea5e9", active: true };

function InvestmentProductsManager({ bankId }) {
  const [items, setItems] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_INVEST_PRODUCT);
  const [delConfirm, setDelConfirm] = useState(null);
  const load = async () => {
    try { const { data } = await api.get(`/banks/${bankId}/investment-products`); setItems(data); }
    catch { setItems([]); }
  };
  useEffect(() => { load(); }, [bankId]);

  const openNew = () => { setEditItem("new"); setForm(EMPTY_INVEST_PRODUCT); };
  const openEdit = (p) => { setEditItem(p); setForm({ ...EMPTY_INVEST_PRODUCT, ...p }); };
  const save = async () => {
    const payload = { name: form.name, description: form.description, color: form.color, active: form.active };
    try {
      if (editItem === "new") await api.post(`/banks/${bankId}/investment-products`, payload);
      else await api.patch(`/banks/${bankId}/investment-products/${editItem.id}`, payload);
      toast.success("Offre enregistrée"); setEditItem(null); load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const remove = async (p) => {
    try { await api.delete(`/banks/${bankId}/investment-products/${p.id}`); toast.success("Offre supprimée"); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold">Catalogue d'investissements ({items.length})</h2>
        <Button size="sm" className="bg-zinc-900 text-white" onClick={openNew} data-testid="invest-product-new"><Plus className="w-4 h-4 mr-1" />Nouvelle offre</Button>
      </div>
      <p className="text-xs text-zinc-400">Le rendement personnalisé par client se configure dans l'onglet « Demandes des clients ».</p>
      <div className="grid md:grid-cols-3 gap-3">
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-zinc-200 rounded-xl p-4" style={{ borderLeft: `4px solid ${p.color}` }}>
            <div className="font-bold text-sm flex items-center gap-2">{p.name}{p.active === false && <span className="text-xs bg-zinc-200 text-zinc-500 px-2 py-0.5 rounded-full">Masqué</span>}</div>
            <div className="text-xs text-zinc-500 mt-1 mb-3">{p.description}</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="text-xs" onClick={() => openEdit(p)}><Pencil className="w-3.5 h-3.5 mr-1" />Modifier</Button>
              <Button size="sm" variant="outline" className="text-xs text-red-600" onClick={() => setDelConfirm(p)}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-zinc-400 md:col-span-3">Aucune offre d'investissement.</div>}
      </div>

      <Dialog open={!!editItem} onOpenChange={(o) => { if (!o) setEditItem(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editItem === "new" ? "Nouvelle offre d'investissement" : "Modifier l'offre"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Immobilier, Crypto…" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Couleur</Label><ColorPicker value={form.color} onChange={(c) => setForm({ ...form, color: c })} /></div>
            <div className="flex items-center gap-3"><Switch checked={form.active !== false} onCheckedChange={(v) => setForm({ ...form, active: v })} /><label className="text-sm">Visible par les clients</label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>Annuler</Button>
            <Button className="bg-zinc-900 text-white" onClick={save} disabled={!form.name}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!delConfirm} title="Supprimer l'offre" message={`Supprimer définitivement l'offre « ${delConfirm?.name} » ?`} onOk={() => { remove(delConfirm); setDelConfirm(null); }} onCancel={() => setDelConfirm(null)} />
    </div>
  );
}

/* ── Financing products catalog ──────────────────────────────────────────── */
const EMPTY_FIN_PRODUCT = { name: "", description: "", type: "remboursable", color: "#0ea5e9", active: true };

function FinancingProductsManager({ bankId }) {
  const [items, setItems] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FIN_PRODUCT);
  const [delConfirm, setDelConfirm] = useState(null);
  const load = async () => {
    try { const { data } = await api.get(`/banks/${bankId}/financing-products`); setItems(data); }
    catch { setItems([]); }
  };
  useEffect(() => { load(); }, [bankId]);

  const openNew = () => { setEditItem("new"); setForm(EMPTY_FIN_PRODUCT); };
  const openEdit = (p) => { setEditItem(p); setForm({ ...EMPTY_FIN_PRODUCT, ...p }); };
  const save = async () => {
    const payload = { name: form.name, description: form.description, type: form.type, color: form.color, active: form.active };
    try {
      if (editItem === "new") await api.post(`/banks/${bankId}/financing-products`, payload);
      else await api.patch(`/banks/${bankId}/financing-products/${editItem.id}`, payload);
      toast.success("Offre enregistrée"); setEditItem(null); load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const remove = async (p) => {
    try { await api.delete(`/banks/${bankId}/financing-products/${p.id}`); toast.success("Offre supprimée"); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold">Catalogue de financements ({items.length})</h2>
        <Button size="sm" className="bg-zinc-900 text-white" onClick={openNew} data-testid="fin-product-new"><Plus className="w-4 h-4 mr-1" />Nouvelle offre</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-zinc-200 rounded-xl p-4" style={{ borderLeft: `4px solid ${p.color}` }}>
            <div className="font-bold text-sm flex items-center gap-2">{p.name}
              <span className="text-xs bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full">{p.type === "remboursable" ? "Remboursable" : "Non remboursable"}</span>
              {p.active === false && <span className="text-xs bg-zinc-200 text-zinc-500 px-2 py-0.5 rounded-full">Masqué</span>}
            </div>
            <div className="text-xs text-zinc-500 mt-1 mb-3">{p.description}</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="text-xs" onClick={() => openEdit(p)}><Pencil className="w-3.5 h-3.5 mr-1" />Modifier</Button>
              <Button size="sm" variant="outline" className="text-xs text-red-600" onClick={() => setDelConfirm(p)}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-zinc-400 md:col-span-2">Aucune offre de financement.</div>}
      </div>

      <Dialog open={!!editItem} onOpenChange={(o) => { if (!o) setEditItem(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editItem === "new" ? "Nouvelle offre de financement" : "Modifier l'offre"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Financement d'entreprise…" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="remboursable">Remboursable</SelectItem>
                  <SelectItem value="non-remboursable">Non remboursable</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Couleur</Label><ColorPicker value={form.color} onChange={(c) => setForm({ ...form, color: c })} /></div>
            <div className="flex items-center gap-3"><Switch checked={form.active !== false} onCheckedChange={(v) => setForm({ ...form, active: v })} /><label className="text-sm">Visible par les clients</label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>Annuler</Button>
            <Button className="bg-zinc-900 text-white" onClick={save} disabled={!form.name}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!delConfirm} title="Supprimer l'offre" message={`Supprimer définitivement l'offre « ${delConfirm?.name} » ?`} onOk={() => { remove(delConfirm); setDelConfirm(null); }} onCancel={() => setDelConfirm(null)} />
    </div>
  );
}

function BankCreditRequests({ bankId }) {
  const [items, setItems] = useState([]);
  const [viewItem, setViewItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [clearConfirm, setClearConfirm] = useState(false);
  const load = async () => {
    try { const { data } = await api.get(`/banks/${bankId}/credit-requests`); setItems(data); }
    catch (_) { setItems([]); }
  };
  useEffect(() => { load(); }, [bankId]);

  const update = async (id, action) => {
    try {
      await api.post(`/banks/${bankId}/credit-requests/${id}/${action}`);
      toast.success(action === "approve" ? "Crédit approuvé — dépôt initial prélevé" : "Demande rejetée");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const openEdit = (c) => {
    setEditItem(c);
    setEditForm({
      product_name: c.product_name || "",
      amount: c.amount ?? 0,
      duration_months: c.duration_months ?? 0,
      initial_deposit: c.initial_deposit ?? 0,
      rate: c.rate ?? "",
      purpose: c.purpose || "",
      status: c.status || "pending",
    });
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/banks/${bankId}/credit-requests/${editItem.id}`, {
        product_name: editForm.product_name,
        amount: Number(editForm.amount),
        duration_months: Number(editForm.duration_months),
        initial_deposit: Number(editForm.initial_deposit),
        rate: editForm.rate === "" ? null : Number(editForm.rate),
        purpose: editForm.purpose,
        status: editForm.status,
      });
      toast.success("Crédit mis à jour");
      setEditItem(null);
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const clearAll = async () => {
    try { await api.delete(`/banks/${bankId}/credit-requests`); toast.success("Historique des crédits supprimé"); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold">Demandes de crédit ({items.length})</h2>
        {items.length > 0 && (
          <Button size="sm" variant="outline" className="text-red-600 text-xs" onClick={() => setClearConfirm(true)} data-testid="credits-clear">
            <Trash2 className="w-3.5 h-3.5 mr-1" />Supprimer l'historique
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-zinc-400">Aucune demande de crédit.</div>
      ) : (
        items.map((c) => (
          <div key={c.id} className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-wrap items-center gap-4">
            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div className="flex-1 min-w-[180px]">
              <div className="font-bold text-sm">{c.product_name}</div>
              <div className="text-xs text-zinc-500">{c.bank_user_name} · {c.duration_months} mois · dépôt {fmtMoney(c.initial_deposit || 0, "EUR")} · {new Date(c.created_at).toLocaleDateString("fr-FR")}</div>
              {c.purpose && <div className="text-xs text-zinc-600 mt-1 italic">"{c.purpose}"</div>}
            </div>
            <div className="font-mono-num font-bold text-sm">{fmtMoney(c.amount, "EUR")}</div>
            <StatusBadge status={c.status} labels={{ ok: "Accepté", no: "Refusé" }} />
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => setViewItem(c)} className="text-xs" data-testid={`credit-view-${c.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Voir</Button>
              <Button size="sm" variant="outline" onClick={() => openEdit(c)} className="text-xs" data-testid={`credit-edit-${c.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Modifier</Button>
              {c.status === "pending" && (
                <>
                  <Button size="sm" onClick={() => update(c.id, "approve")} className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`credit-approve-${c.id}`}><Check className="w-3.5 h-3.5 mr-1" />Approuver</Button>
                  <Button size="sm" variant="outline" onClick={() => update(c.id, "reject")} className="text-red-600 text-xs" data-testid={`credit-reject-${c.id}`}><X className="w-3.5 h-3.5 mr-1" />Rejeter</Button>
                </>
              )}
            </div>
          </div>
        ))
      )}

      {/* Voir les modalités */}
      <Dialog open={!!viewItem} onOpenChange={(o) => { if (!o) setViewItem(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Modalités du crédit</DialogTitle></DialogHeader>
          {viewItem && (
            <div className="py-2">
              <DetailRow label="Client" value={viewItem.bank_user_name} />
              <DetailRow label="Produit" value={viewItem.product_name} />
              <DetailRow label="Montant" value={fmtMoney(viewItem.amount, "EUR")} />
              <DetailRow label="Durée" value={`${viewItem.duration_months} mois`} />
              <DetailRow label="Dépôt initial" value={fmtMoney(viewItem.initial_deposit || 0, "EUR")} />
              {viewItem.rate != null && <DetailRow label="Taux" value={`${viewItem.rate} %`} />}
              <DetailRow label="Motif" value={viewItem.purpose} />
              <DetailRow label="Statut" value={viewItem.status} />
              <DetailRow label="Demandé le" value={new Date(viewItem.created_at).toLocaleString("fr-FR")} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modifier les caractéristiques */}
      <Dialog open={!!editItem} onOpenChange={(o) => { if (!o) setEditItem(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Modifier le crédit</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Produit</Label><Input value={editForm.product_name || ""} onChange={(e) => setEditForm({ ...editForm, product_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Montant (€)</Label><Input type="number" min={0} value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} /></div>
              <div><Label>Durée (mois)</Label><Input type="number" min={0} value={editForm.duration_months} onChange={(e) => setEditForm({ ...editForm, duration_months: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Dépôt initial (€)</Label><Input type="number" min={0} value={editForm.initial_deposit} onChange={(e) => setEditForm({ ...editForm, initial_deposit: e.target.value })} /></div>
              <div><Label>Taux (%)</Label><Input type="number" min={0} step={0.01} value={editForm.rate} onChange={(e) => setEditForm({ ...editForm, rate: e.target.value })} placeholder="ex: 2.5" /></div>
            </div>
            <div><Label>Motif</Label><Textarea rows={2} value={editForm.purpose || ""} onChange={(e) => setEditForm({ ...editForm, purpose: e.target.value })} /></div>
            <div>
              <Label>Statut</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="approved">Accepté</SelectItem>
                  <SelectItem value="rejected">Refusé</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-400 mt-1">Le changement de statut ici ne prélève pas le dépôt. Utilisez « Approuver » pour prélever le dépôt initial.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>Annuler</Button>
            <Button className="bg-zinc-900 text-white" onClick={saveEdit}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={clearConfirm} title="Supprimer l'historique des crédits" message="Supprimer définitivement toutes les demandes de crédit de cette banque ? Cette action est irréversible." onOk={() => { setClearConfirm(false); clearAll(); }} onCancel={() => setClearConfirm(false)} />
    </div>
  );
}

function BankInvestmentRequests({ bankId }) {
  const [items, setItems] = useState([]);
  const [viewItem, setViewItem] = useState(null);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [yieldItem, setYieldItem] = useState(null);
  const [yieldForm, setYieldForm] = useState({ daily_rate: "", start_date: "", end_date: "", amount: "" });
  const load = async () => {
    try { const { data } = await api.get(`/banks/${bankId}/investment-requests`); setItems(data); }
    catch (_) { setItems([]); }
  };
  useEffect(() => { load(); }, [bankId]);

  const update = async (id, action) => {
    try {
      await api.post(`/banks/${bankId}/investment-requests/${id}/${action}`);
      toast.success(action === "approve" ? "Investissement validé — montant prélevé" : "Demande rejetée");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const toDateInput = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; }
  };
  const openYield = (c) => {
    setYieldItem(c);
    setYieldForm({
      daily_rate: c.daily_rate ?? "",
      start_date: toDateInput(c.start_date) || new Date().toISOString().slice(0, 10),
      end_date: toDateInput(c.end_date),
      amount: c.amount ?? "",
    });
  };
  const saveYield = async () => {
    try {
      await api.patch(`/banks/${bankId}/investment-requests/${yieldItem.id}`, {
        daily_rate: yieldForm.daily_rate === "" ? null : Number(yieldForm.daily_rate),
        start_date: yieldForm.start_date ? new Date(yieldForm.start_date).toISOString() : null,
        end_date: yieldForm.end_date ? new Date(yieldForm.end_date).toISOString() : null,
        amount: yieldForm.amount === "" ? null : Number(yieldForm.amount),
      });
      toast.success("Rendement configuré");
      setYieldItem(null);
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const clearAll = async () => {
    try { await api.delete(`/banks/${bankId}/investment-requests`); toast.success("Historique des investissements supprimé"); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold">Demandes d'investissement ({items.length})</h2>
        {items.length > 0 && (
          <Button size="sm" variant="outline" className="text-red-600 text-xs" onClick={() => setClearConfirm(true)} data-testid="invests-clear">
            <Trash2 className="w-3.5 h-3.5 mr-1" />Supprimer l'historique
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-zinc-400">Aucune demande d'investissement.</div>
      ) : (
        items.map((c) => (
          <div key={c.id} className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-wrap items-center gap-4">
            <Briefcase className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <div className="flex-1 min-w-[180px]">
              <div className="font-bold text-sm">{c.product_name}</div>
              <div className="text-xs text-zinc-500">{c.bank_user_name} · {new Date(c.created_at).toLocaleDateString("fr-FR")}</div>
              {c.daily_rate > 0 && <div className="text-xs text-emerald-600 font-medium mt-0.5">{c.daily_rate}%/jour · profit {fmtMoney(c.profit || 0, "EUR")}</div>}
              {c.notes && <div className="text-xs text-zinc-600 mt-1 italic">"{c.notes}"</div>}
            </div>
            <div className="font-mono-num font-bold text-sm">{fmtMoney(c.amount, "EUR")}</div>
            <StatusBadge status={c.status} labels={{ ok: "Validé", no: "Refusé" }} />
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => setViewItem(c)} className="text-xs" data-testid={`invest-view-${c.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Voir</Button>
              <Button size="sm" variant="outline" onClick={() => openYield(c)} className="text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid={`invest-yield-${c.id}`}><TrendingUp className="w-3.5 h-3.5 mr-1" />Rendement</Button>
              {c.status === "pending" && (
                <>
                  <Button size="sm" onClick={() => update(c.id, "approve")} className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`invest-approve-${c.id}`}><Check className="w-3.5 h-3.5 mr-1" />Approuver</Button>
                  <Button size="sm" variant="outline" onClick={() => update(c.id, "reject")} className="text-red-600 text-xs" data-testid={`invest-reject-${c.id}`}><X className="w-3.5 h-3.5 mr-1" />Rejeter</Button>
                </>
              )}
            </div>
          </div>
        ))
      )}

      <Dialog open={!!viewItem} onOpenChange={(o) => { if (!o) setViewItem(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Modalités de l'investissement</DialogTitle></DialogHeader>
          {viewItem && (
            <div className="py-2">
              <DetailRow label="Client" value={viewItem.bank_user_name} />
              <DetailRow label="Produit" value={viewItem.product_name} />
              <DetailRow label="Montant" value={fmtMoney(viewItem.amount, "EUR")} />
              <DetailRow label="Profit" value={fmtMoney(viewItem.profit || 0, "EUR")} />
              <DetailRow label="Note" value={viewItem.notes} />
              <DetailRow label="Statut" value={viewItem.status} />
              <DetailRow label="Rendement / jour" value={viewItem.daily_rate ? `${viewItem.daily_rate} %` : "—"} />
              {viewItem.start_date && <DetailRow label="Début" value={new Date(viewItem.start_date).toLocaleDateString("fr-FR")} />}
              {viewItem.end_date && <DetailRow label="Fin" value={new Date(viewItem.end_date).toLocaleDateString("fr-FR")} />}
              <DetailRow label="Demandé le" value={new Date(viewItem.created_at).toLocaleString("fr-FR")} />
              <p className="text-xs text-zinc-400 mt-3">En approuvant, le montant est prélevé du solde du client.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Configurer le rendement personnalisé */}
      <Dialog open={!!yieldItem} onOpenChange={(o) => { if (!o) setYieldItem(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Rendement personnalisé — {yieldItem?.bank_user_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-zinc-500">Définissez un gain journalier pour cet investissement. Les bénéfices sont calculés automatiquement entre la date de début et aujourd'hui (ou la date de fin).</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Gain journalier (%)</Label><Input type="number" step={0.01} min={0} value={yieldForm.daily_rate} onChange={(e) => setYieldForm({ ...yieldForm, daily_rate: e.target.value })} placeholder="ex: 2" /></div>
              <div><Label>Montant investi (€)</Label><Input type="number" min={0} value={yieldForm.amount} onChange={(e) => setYieldForm({ ...yieldForm, amount: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date de début</Label><Input type="date" value={yieldForm.start_date} onChange={(e) => setYieldForm({ ...yieldForm, start_date: e.target.value })} /></div>
              <div><Label>Date de fin</Label><Input type="date" value={yieldForm.end_date} onChange={(e) => setYieldForm({ ...yieldForm, end_date: e.target.value })} /></div>
            </div>
            {yieldForm.daily_rate && yieldForm.amount && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800">
                Estimation : <strong>{fmtMoney((Number(yieldForm.amount) * Number(yieldForm.daily_rate) / 100), "EUR")}</strong> de profit par jour.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setYieldItem(null)}>Annuler</Button>
            <Button className="bg-zinc-900 text-white" onClick={saveYield}>Enregistrer le rendement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={clearConfirm} title="Supprimer l'historique des investissements" message="Supprimer définitivement toutes les demandes d'investissement de cette banque ? Cette action est irréversible." onOk={() => { setClearConfirm(false); clearAll(); }} onCancel={() => setClearConfirm(false)} />
    </div>
  );
}

function BankFinancingRequests({ bankId }) {
  const [items, setItems] = useState([]);
  const [viewItem, setViewItem] = useState(null);
  const [clearConfirm, setClearConfirm] = useState(false);
  const load = async () => {
    try { const { data } = await api.get(`/banks/${bankId}/financing-requests`); setItems(data); }
    catch (_) { setItems([]); }
  };
  useEffect(() => { load(); }, [bankId]);

  const update = async (id, action) => {
    try {
      await api.post(`/banks/${bankId}/financing-requests/${id}/${action}`);
      toast.success(action === "approve" ? "Financement accordé — fonds crédités" : "Demande rejetée");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const clearAll = async () => {
    try { await api.delete(`/banks/${bankId}/financing-requests`); toast.success("Historique des financements supprimé"); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold">Demandes de financement ({items.length})</h2>
        {items.length > 0 && (
          <Button size="sm" variant="outline" className="text-red-600 text-xs" onClick={() => setClearConfirm(true)} data-testid="financings-clear">
            <Trash2 className="w-3.5 h-3.5 mr-1" />Supprimer l'historique
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-zinc-400">Aucune demande de financement.</div>
      ) : (
        items.map((c) => (
          <div key={c.id} className="bg-white border border-zinc-200 rounded-xl p-4">
            <div className="flex flex-wrap items-center gap-4 mb-2">
              <HandCoins className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <div className="flex-1 min-w-[180px]">
                <div className="font-bold text-sm">{c.project_name}</div>
                <div className="text-xs text-zinc-500">{c.bank_user_name} · {c.type === "remboursable" ? "Remboursable" : "Non remboursable"} · {c.sector || "—"}</div>
              </div>
              <div className="font-mono-num font-bold text-sm">{fmtMoney(c.amount, "EUR")}</div>
              <StatusBadge status={c.status} labels={{ ok: "Accordé", no: "Refusé" }} />
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setViewItem(c)} className="text-xs" data-testid={`fin-view-${c.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Voir</Button>
                {c.status === "pending" && (
                  <>
                    <Button size="sm" onClick={() => update(c.id, "approve")} className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`fin-approve-${c.id}`}><Check className="w-3.5 h-3.5 mr-1" />Approuver</Button>
                    <Button size="sm" variant="outline" onClick={() => update(c.id, "reject")} className="text-red-600 text-xs" data-testid={`fin-reject-${c.id}`}><X className="w-3.5 h-3.5 mr-1" />Rejeter</Button>
                  </>
                )}
              </div>
            </div>
            <div className="text-xs text-zinc-600 italic pl-9">"{c.description}"</div>
          </div>
        ))
      )}

      <Dialog open={!!viewItem} onOpenChange={(o) => { if (!o) setViewItem(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Modalités du financement</DialogTitle></DialogHeader>
          {viewItem && (
            <div className="py-2">
              <DetailRow label="Client" value={viewItem.bank_user_name} />
              <DetailRow label="Projet" value={viewItem.project_name} />
              <DetailRow label="Type" value={viewItem.type === "remboursable" ? "Remboursable" : "Non remboursable"} />
              <DetailRow label="Secteur" value={viewItem.sector} />
              <DetailRow label="Montant" value={fmtMoney(viewItem.amount, "EUR")} />
              <DetailRow label="Description" value={viewItem.description} />
              <DetailRow label="Statut" value={viewItem.status} />
              <DetailRow label="Demandé le" value={new Date(viewItem.created_at).toLocaleString("fr-FR")} />
              <p className="text-xs text-zinc-400 mt-3">En approuvant, le montant est crédité sur le solde du client.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={clearConfirm} title="Supprimer l'historique des financements" message="Supprimer définitivement toutes les demandes de financement de cette banque ? Cette action est irréversible." onOk={() => { setClearConfirm(false); clearAll(); }} onCancel={() => setClearConfirm(false)} />
    </div>
  );
}

function BankLanding({ bank, reload }) {
  const [form, setForm] = useState({
    hero_title: bank.hero_title || "",
    hero_subtitle: bank.hero_subtitle || "",
    cta_text: bank.cta_text || "Ouvrir un compte",
    features: bank.features?.length ? bank.features : [{ title: "", body: "" }, { title: "", body: "" }, { title: "", body: "" }],
    signup_enabled: bank.signup_enabled !== false,
    transfer_validation_required: bank.transfer_validation_required !== false,
    contact_email: bank.contact_email || "",
    contact_phone: bank.contact_phone || "",
    bank_address: bank.bank_address || "",
    facebook_url: bank.facebook_url || "",
    instagram_url: bank.instagram_url || "",
    tiktok_url: bank.tiktok_url || "",
    whatsapp_url: bank.whatsapp_url || "",
    twitter_url: bank.twitter_url || "",
    linkedin_url: bank.linkedin_url || "",
    threads_url: bank.threads_url || "",
  });

  const [translating, setTranslating] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    try { await api.put(`/banks/${bank.id}`, form); toast.success("Page d'accueil mise à jour"); reload(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const autoTranslate = async () => {
    setTranslating(true);
    try {
      await api.post(`/banks/${bank.id}/translate`, { source_locale: bank.default_locale || "fr" });
      toast.success("Contenu traduit en 10 langues avec succès");
      reload();
    } catch (err) { toast.error(formatError(err)); }
    finally { setTranslating(false); }
  };

  const updFeature = (i, k, v) => {
    const fs = [...form.features]; fs[i] = { ...fs[i], [k]: v }; setForm({ ...form, features: fs });
  };
  return (
    <form onSubmit={save} className="bg-white border border-zinc-200 rounded-xl p-6 space-y-6 max-w-3xl">
      <div>
        <h2 className="font-display text-xl font-bold mb-1">Personnalisation de la page d'accueil</h2>
        <p className="text-sm text-zinc-500">Cette page sera affichée sur <span className="font-mono-num">{bank.slug}.{bank.banks_domain || BANKS_DOMAIN}</span></p>
      </div>

      <p className="text-xs text-zinc-500 -mt-2">
        Le nom, le logo et le slogan de la banque se modifient dans l'onglet <span className="font-semibold">Branding</span>.
      </p>

      {/* ── Hero ── */}
      <div className="space-y-4 p-4 rounded-lg border border-zinc-100 bg-zinc-50">
        <h3 className="font-semibold text-sm text-zinc-700">Section Hero</h3>
        <div><Label>Titre principal</Label><Input value={form.hero_title} onChange={(e) => setForm({ ...form, hero_title: e.target.value })} placeholder="Bienvenue sur ma banque" data-testid="landing-hero-title" /></div>
        <div><Label>Sous-titre</Label><Textarea value={form.hero_subtitle} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} rows={2} data-testid="landing-hero-subtitle" /></div>
        <div><Label>Texte du bouton CTA</Label><Input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} data-testid="landing-cta" /></div>
      </div>

      {/* ── Fonctionnalités ── */}
      <div className="space-y-3 p-4 rounded-lg border border-zinc-100 bg-zinc-50">
        <h3 className="font-semibold text-sm text-zinc-700">3 blocs de fonctionnalités</h3>
        {form.features.slice(0, 3).map((f, i) => (
          <div key={i} className="grid md:grid-cols-2 gap-2 p-3 rounded-lg border border-zinc-200 bg-white">
            <Input value={f.title || ""} onChange={(e) => updFeature(i, "title", e.target.value)} placeholder={`Titre bloc ${i + 1}`} data-testid={`landing-f${i}-title`} />
            <Input value={f.body || ""} onChange={(e) => updFeature(i, "body", e.target.value)} placeholder={`Description bloc ${i + 1}`} data-testid={`landing-f${i}-body`} />
          </div>
        ))}
      </div>

      {/* ── Coordonnées ── */}
      <div className="space-y-4 p-4 rounded-lg border border-zinc-100 bg-zinc-50">
        <h3 className="font-semibold text-sm text-zinc-700">Coordonnées (footer)</h3>
        <div><Label>Email de contact</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="contact@mabanque.com" data-testid="landing-contact-email" /></div>
        <div><Label>Téléphone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+33 1 23 45 67 89" data-testid="landing-contact-phone" /></div>
        <div><Label>Adresse</Label><Input value={form.bank_address} onChange={(e) => setForm({ ...form, bank_address: e.target.value })} placeholder="12 Rue de la Finance, Paris" data-testid="landing-address" /></div>
      </div>

      {/* ── Réseaux sociaux ── */}
      <div className="space-y-4 p-4 rounded-lg border border-zinc-100 bg-zinc-50">
        <h3 className="font-semibold text-sm text-zinc-700">Réseaux sociaux (footer)</h3>
        <div><Label>Facebook</Label><Input value={form.facebook_url} onChange={(e) => setForm({ ...form, facebook_url: e.target.value })} placeholder="https://facebook.com/mabanque" data-testid="landing-facebook" /></div>
        <div><Label>Instagram</Label><Input value={form.instagram_url} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} placeholder="https://instagram.com/mabanque" data-testid="landing-instagram" /></div>
        <div><Label>TikTok</Label><Input value={form.tiktok_url} onChange={(e) => setForm({ ...form, tiktok_url: e.target.value })} placeholder="https://tiktok.com/@mabanque" data-testid="landing-tiktok" /></div>
        <div><Label>WhatsApp (lien wa.me ou numéro)</Label><Input value={form.whatsapp_url} onChange={(e) => setForm({ ...form, whatsapp_url: e.target.value })} placeholder="https://wa.me/33612345678" data-testid="landing-whatsapp" /></div>
        <div><Label>X (Twitter)</Label><Input value={form.twitter_url} onChange={(e) => setForm({ ...form, twitter_url: e.target.value })} placeholder="https://x.com/mabanque" data-testid="landing-twitter" /></div>
        <div><Label>LinkedIn</Label><Input value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} placeholder="https://linkedin.com/company/mabanque" data-testid="landing-linkedin" /></div>
        <div><Label>Threads</Label><Input value={form.threads_url} onChange={(e) => setForm({ ...form, threads_url: e.target.value })} placeholder="https://threads.net/@mabanque" data-testid="landing-threads" /></div>
      </div>

      {/* ── Options ── */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 p-3 rounded-lg border border-zinc-200">
          <Switch checked={form.signup_enabled} onCheckedChange={(v) => setForm({ ...form, signup_enabled: v })} data-testid="landing-signup-enabled" />
          <div>
            <div className="font-semibold text-sm">Inscriptions ouvertes</div>
            <div className="text-xs text-zinc-500">Si désactivé, le bouton d'inscription est masqué.</div>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border border-zinc-200">
          <Switch checked={form.transfer_validation_required} onCheckedChange={(v) => setForm({ ...form, transfer_validation_required: v })} data-testid="landing-tv-required" />
          <div>
            <div className="font-semibold text-sm">Code de validation pour les virements sortants</div>
            <div className="text-xs text-zinc-500">L'admin doit transmettre un code 6 chiffres au client avant que le virement parte.</div>
          </div>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Button type="submit" className="bg-zinc-900 hover:bg-zinc-700 text-white" data-testid="landing-save">Enregistrer</Button>
        <Button type="button" variant="outline" onClick={autoTranslate} disabled={translating} data-testid="landing-translate">
          {translating ? "Traduction en cours…" : "Traduire le contenu automatiquement"}
        </Button>
      </div>
      <p className="text-xs text-zinc-400">La traduction automatique génère des versions dans toutes les langues supportées par la plateforme (40+ langues). Propulsé par Claude Sonnet 4.5 pour une qualité professionnelle.</p>
    </form>
  );
}

function BankLocalizedLinks({ bank }) {
  const DESTINATIONS = [
    { value: "home", label: "Page d'accueil", suffix: "" },
    { value: "login", label: "Page de connexion", suffix: "/login" },
  ];
  const [dest, setDest] = useState("home");
  const [lang, setLang] = useState(bank.default_locale || "fr");
  const destObj = DESTINATIONS.find((d) => d.value === dest) || DESTINATIONS[0];
  const langObj = LANGUAGES.find((l) => l.value === lang);
  const base = bankPublicUrl(bank.slug, destObj.suffix, bank.banks_domain);
  const url = `${base}?lang=${lang}`;
  const copy = () => { navigator.clipboard?.writeText(url); toast.success("Lien copié"); };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="font-display text-xl font-bold mb-1">Liens localisés</h2>
        <p className="text-sm text-zinc-500">
          Choisissez la langue parlée par votre client, puis envoyez-lui le lien généré.
          Lorsqu'il l'ouvrira, tout le site s'affichera automatiquement dans cette langue —
          sans qu'il ait à la sélectionner lui-même.
        </p>
      </div>
      <div className="bg-white border border-zinc-200 rounded-xl p-6 space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Destination</Label>
            <Select value={dest} onValueChange={setDest}>
              <SelectTrigger data-testid="link-dest"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DESTINATIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Langue du client</Label>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger data-testid="link-lang"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Lien généré {langObj ? `(${langObj.label})` : ""}</Label>
          <div className="bg-zinc-100 p-3 rounded font-mono-num text-sm break-all mt-1" data-testid="localized-link-text">{url}</div>
        </div>
        <div className="flex gap-2">
          <Button onClick={copy} className="bg-zinc-900 hover:bg-zinc-700 text-white" data-testid="localized-link-copy">
            <LinkIcon className="w-4 h-4 mr-1" /> Copier le lien
          </Button>
          <Button variant="outline" asChild>
            <a href={url} target="_blank" rel="noopener noreferrer" data-testid="localized-link-open">
              <Eye className="w-4 h-4 mr-1" /> Aperçu
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}

function BankDepositSettings({ bank, reload }) {
  const [instructions, setInstructions] = useState(bank.deposit_instructions || "");
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/banks/${bank.id}`, { deposit_instructions: instructions });
      toast.success("Instructions de dépôt mises à jour");
      reload();
    } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="font-display text-xl font-bold mb-1">Modalités de dépôt</h2>
        <p className="text-sm text-zinc-500">Ce message s'affiche lorsqu'un client clique sur le bouton <strong>Dépôt</strong> dans son espace. Indiquez vos coordonnées bancaires, instructions de virement ou tout autre information utile.</p>
      </div>
      <form onSubmit={save} className="bg-white border border-zinc-200 rounded-xl p-6 space-y-4">
        <div>
          <Label>Instructions de dépôt</Label>
          <Textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={8}
            placeholder={"Pour effectuer un dépôt, virez les fonds sur l'IBAN suivant :\nIBAN : FR76 0000 0000 0000 0000 0000 000\nTitulaire : Nom de la banque"}
            data-testid="deposit-instructions-textarea"
            className="mt-1 font-mono text-sm"
          />
          <p className="text-xs text-zinc-500 mt-1">Laissez vide pour afficher le message par défaut.</p>
        </div>
        <Button type="submit" className="bg-zinc-900 hover:bg-zinc-700 text-white" data-testid="deposit-instructions-save">Enregistrer</Button>
      </form>
    </div>
  );
}

// Legacy txns view (history)
function BankTxns({ bankId, users, txns, reload }) {
  return (
    <div>
      <h2 className="font-display text-xl font-bold mb-4">Historique des opérations ({txns.length})</h2>
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-4">Utilisateur</th><th className="text-left p-4">Libellé</th><th className="text-left p-4">Type</th><th className="text-right p-4">Montant</th><th className="text-right p-4">Solde</th><th className="text-right p-4">Date</th></tr>
          </thead>
          <tbody>
            {txns.map((t) => (
              <tr key={t.id} className="border-t border-zinc-100">
                <td className="p-4">{t.bank_user_name}</td>
                <td className="p-4">{t.label}</td>
                <td className="p-4"><Badge variant="outline" className={t.type === "credit" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}>{t.op || t.type}</Badge></td>
                <td className={`p-4 text-right font-mono-num ${t.type === "credit" ? "text-emerald-600" : "text-red-600"}`}>{t.type === "credit" ? "+" : "-"}{fmtMoney(t.amount)}</td>
                <td className="p-4 text-right font-mono-num">{fmtMoney(t.balance_after)}</td>
                <td className="p-4 text-right text-zinc-400 text-xs">{new Date(t.created_at).toLocaleString("fr-FR")}</td>
              </tr>
            ))}
            {!txns.length && <tr><td colSpan={6} className="p-8 text-center text-zinc-400">Aucune transaction.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function BankBranding({ bank, templates, reload }) {
  const [form, setForm] = useState({
    name: bank.name, tagline: bank.tagline, logo_url: bank.logo_url,
    primary_color: bank.primary_color, template_id: bank.template_id,
    default_currency: bank.default_currency, default_locale: bank.default_locale,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(bank.logo_url || "");
  const [uploading, setUploading] = useState(false);
  const [hasPremiumAccess, setHasPremiumAccess] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    api.get("/subscriptions", { params: { status: "active" } })
      .then((r) => setHasPremiumAccess((r.data || []).length > 0))
      .catch(() => {});
  }, []);

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const uploadLogo = async () => {
    if (!logoFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", logoFile);
      // axios attaches the admin Bearer token via the request interceptor
      const { data } = await api.post(`/banks/${bank.id}/logo`, fd);
      setLogoPreview(data.logo_url);
      setForm((f) => ({ ...f, logo_url: data.logo_url }));
      setLogoFile(null);
      toast.success("Logo mis à jour");
      reload();
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    try { await api.put(`/banks/${bank.id}`, form); toast.success("Branding mis à jour"); reload(); }
    catch (err) { toast.error(formatError(err)); }
  };
  return (
    <form onSubmit={save} className="bg-white border border-zinc-200 rounded-xl p-4 sm:p-6 space-y-4 max-w-3xl branding-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div>
          <Label>Couleur</Label>
          <div className="flex gap-2 items-center">
            <Input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-14 h-10 p-1 shrink-0" />
            <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="flex-1 min-w-0 font-mono-num" />
          </div>
        </div>
      </div>
      <div><Label>Slogan</Label><Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></div>
      <div>
        <Label>Logo</Label>
        <div className="flex flex-wrap items-start gap-4 mt-1">
          {logoPreview
            ? <img src={logoPreview} alt="logo" style={{ height: 48, maxWidth: 120, objectFit: "contain", borderRadius: 8, border: "1px solid #e5e7eb" }} />
            : <div style={{ width: 48, height: 48, borderRadius: 8, background: "#f4f4f5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#a1a1aa" }}>🏦</div>
          }
          <div className="flex flex-col gap-2 shrink-0">
            <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoFile} />
            <Button type="button" variant="outline" size="sm" onClick={() => logoInputRef.current?.click()}>
              <ImageIcon size={14} className="mr-1" /> Choisir un fichier
            </Button>
            {logoFile && (
              <Button type="button" size="sm" className="bg-zinc-900 text-white hover:bg-zinc-700" onClick={uploadLogo} disabled={uploading}>
                {uploading ? "Upload..." : "Uploader le logo"}
              </Button>
            )}
          </div>
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs text-zinc-400">ou URL directe</Label>
            <Input value={form.logo_url} onChange={(e) => { setForm({ ...form, logo_url: e.target.value }); setLogoPreview(e.target.value); }} placeholder="https://..." className="mt-1 w-full text-xs" />
          </div>
        </div>
      </div>
      <div>
        <Label>Template</Label>
        {(() => {
          const groups = [...new Set(templates.map((t) => t.group || "Autre"))];
          return groups.map((g) => (
            <div key={g} className="mt-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">{g}</p>
              {g === "Premium Signature" && !hasPremiumAccess && (
                <p className="text-[11px] text-amber-600 font-medium mb-2 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Templates exclusifs — abonnement actif requis
                </p>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {templates.filter((t) => (t.group || "Autre") === g).map((t) => {
                  const locked = t.min_coins && !hasPremiumAccess;
                  return (
                    <button type="button" key={t.id}
                      onClick={() => { if (locked) return; setForm({ ...form, template_id: t.id }); }}
                      className={`relative text-left rounded-xl p-3 border-2 transition ${locked ? "cursor-not-allowed opacity-60" : ""} ${!locked && form.template_id === t.id ? "border-zinc-900 ring-2 ring-zinc-900/20" : "border-zinc-200 hover:border-zinc-400"}`}
                      style={{ background: t.preview_bg }}>
                      {locked && (
                        <div className="absolute top-2 right-2 bg-black/60 rounded-full p-0.5">
                          <Lock className="w-3 h-3 text-amber-400" />
                        </div>
                      )}
                      <div className="font-bold text-sm leading-tight" style={{ color: t.primary_color }}>{t.name}</div>
                      <div className="text-[10px] mt-1 opacity-60 leading-snug line-clamp-2" style={{ color: t.primary_color }}>{t.description}</div>
                      {locked && <div className="text-[10px] mt-1 font-bold text-amber-500">Abonnement requis</div>}
                    </button>
                  );
                })}
              </div>
            </div>
          ));
        })()}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="min-w-0">
          <Label>Devise par défaut de la banque</Label>
          <Select value={form.default_currency} onValueChange={(v) => setForm({ ...form, default_currency: v })}>
            <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-[55vh] max-w-[calc(100vw-2rem)] overflow-y-auto">
              {CURRENCIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-0">
          <Label>Langue par défaut de la banque</Label>
          <Select value={form.default_locale} onValueChange={(v) => setForm({ ...form, default_locale: v })}>
            <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-[55vh] max-w-[calc(100vw-2rem)] overflow-y-auto">
              {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Button type="submit" className="bg-zinc-900 hover:bg-zinc-700 text-white" data-testid="branding-save">Enregistrer</Button>
    </form>
  );
}

function MySubscriptions({ reload }) {
  const { t } = useAdminLang();
  const [subs, setSubs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ plan_id: "" });
  const [extraOpen, setExtraOpen] = useState(false);
  const [extraSub, setExtraSub] = useState(null);
  const [upgradeConfirmSub, setUpgradeConfirmSub] = useState(null);
  const [delSubConfirm, setDelSubConfirm] = useState(null);

  const load = () => api.get("/subscriptions").then((r) => setSubs(r.data || []));
  useEffect(() => { load(); api.get("/plans").then((r) => setPlans(r.data || [])); }, []);

  const deleteSub = async (id) => {
    try { await api.delete(`/subscriptions/${id}`); toast.success("Abonnement supprimé"); setDelSubConfirm(null); load(); reload?.(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const purchase = async (e) => {
    e.preventDefault();
    try { await api.post("/subscriptions", form); toast.success(t("subs.activated")); setOpen(false); setForm({ plan_id: "" }); load(); reload?.(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const buyExtra = (sub) => { setExtraSub(sub); setExtraOpen(true); };
  const doExtra = async (qty) => {
    setExtraOpen(false);
    try {
      await api.post(`/subscriptions/${extraSub.id}/extra-users`, { subscription_id: extraSub.id, quantity: Number(qty) });
      const cost = Number(qty) * (extraSub?.plan_extra_user_price || 3000);
      toast.success(`+${qty} ${t("subs.usersAdded")}${fmtMoney(cost, "COINS")} ${t("subs.deducted")}`);
      load();
    }
    catch (e) { toast.error(formatError(e)); }
  };

  const nextPlanFor = (sub) => {
    const sorted = [...plans].filter((p) => p.is_active).sort((a, b) => a.price - b.price);
    const idx = sorted.findIndex((p) => p.id === sub.plan_id);
    return idx >= 0 && idx < sorted.length - 1 ? sorted[idx + 1] : null;
  };

  const upgrade = (sub) => {
    const np = nextPlanFor(sub);
    if (!np) return toast.info("Pas de pack supérieur — à venir.");
    setUpgradeConfirmSub({ sub, np });
  };
  const doUpgrade = async () => {
    const sub = upgradeConfirmSub?.sub;
    setUpgradeConfirmSub(null);
    if (!sub) return;
    try { await api.post(`/subscriptions/${sub.id}/upgrade`); toast.success(t("subs.upgradeActivated")); load(); reload?.(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const payMaintenance = async (subId, period) => {
    try {
      await api.post(`/subscriptions/${subId}/pay-maintenance`, { period });
      toast.success(period === "annual" ? t("maintenance.annualPaid") : t("maintenance.monthlyPaid"));
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="admin-subs">
      <div className="flex justify-between items-end mb-6">
        <div><h1 className="font-display text-3xl font-extrabold tracking-tighter">{t("subs.title")}</h1><p className="text-zinc-500 mt-1">{t("subs.subtitle")}</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-pill bg-zinc-900 hover:bg-zinc-700 text-white" data-testid="sub-buy"><Plus className="w-4 h-4 mr-1" /> {t("subs.buy")}</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{t("subs.buyTitle")}</DialogTitle></DialogHeader>
            <form onSubmit={purchase} className="space-y-4">
              <div>
                <Label>{t("subs.plan")}</Label>
                <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })} required>
                  <SelectTrigger data-testid="buy-plan"><SelectValue placeholder={t("subs.selectPlan")} /></SelectTrigger>
                  <SelectContent>
                    {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {fmtMoney(p.price, "COINS")} ({p.max_users} {t("subs.users")})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter><Button type="submit" className="bg-zinc-900 hover:bg-zinc-700 text-white" data-testid="buy-submit">{t("subs.validate")}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-3">
        {subs.map((s) => {
          const np = nextPlanFor(s);
          return (
            <div key={s.id} className="bg-white border border-zinc-200 rounded-xl p-5 flex flex-wrap items-center gap-4" data-testid={`my-sub-${s.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display text-lg font-bold">{s.plan_name}</span>
                  <Badge className={
                    s.status === "active" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                    s.status === "pending" ? "bg-amber-100 text-amber-700 border-amber-200" :
                    s.status === "upgraded" ? "bg-zinc-200 text-zinc-600" :
                    "bg-zinc-100 text-zinc-600"
                  }>{s.status === "active" ? t("subs.active") : s.status === "pending" ? t("subs.pending") : s.status === "upgraded" ? t("subs.upgraded") : s.status}</Badge>
                  {s.bank_id && <Badge variant="outline">{t("subs.bankCreated")}</Badge>}
                  {!s.bank_id && s.status === "active" && <Badge className="bg-blue-100 text-blue-700">{t("subs.available")}</Badge>}
                </div>
                <div className="text-sm text-zinc-500 mt-1">+{s.extra_users || 0} {t("subs.extraUsers")} • {fmtMoney(s.plan_price, "COINS")} (unique)</div>
                {s.status === "active" && s.maintenance_due_at && (() => {
                  const dueDate = new Date(s.maintenance_due_at);
                  const daysLeft = Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24));
                  if (!s.maintenance_overdue && daysLeft > 7) {
                    return <div className="text-xs text-zinc-400 mt-1">{t("maintenance.label")}: {dueDate.toLocaleDateString("fr-FR")} ({daysLeft > 0 ? `${daysLeft} ${t("maintenance.daysLeft")}` : t("maintenance.today")})</div>;
                  }
                  return (
                    <div className={`mt-2 p-3 rounded-lg border ${s.maintenance_overdue ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
                      <div className={`text-xs font-semibold mb-2 ${s.maintenance_overdue ? "text-red-700" : "text-amber-700"}`}>
                        {s.maintenance_overdue ? t("maintenance.overdue") : `${t("maintenance.dueIn")} ${daysLeft} ${t("maintenance.days")}`}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={() => payMaintenance(s.id, "monthly")}
                          className="bg-zinc-900 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-zinc-700 transition font-semibold">
                          {t("maintenance.pay1month")} ({fmtMoney(s.maintenance_monthly || 2500, "COINS")})
                        </button>
                        <button onClick={() => payMaintenance(s.id, "annual")}
                          className="border border-zinc-300 text-zinc-700 text-xs px-3 py-1.5 rounded-lg hover:bg-zinc-50 transition font-semibold">
                          {t("maintenance.pay1year")} ({fmtMoney(s.maintenance_annual || 30000, "COINS")})
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
              {s.status === "active" && (
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => buyExtra(s)} data-testid={`extra-users-${s.id}`}>{t("subs.addUsers")} ({fmtMoney(s.plan_extra_user_price || 3000, "COINS")}/{t("subs.users")})</Button>
                  {np ? (
                    <Button size="sm" variant="outline" onClick={() => upgrade(s)} className="text-blue-700 border-blue-300 hover:bg-blue-50" data-testid={`upgrade-${s.id}`}>
                      <ArrowUp className="w-4 h-4 mr-1" /> {t("subs.upgradeTo")} {np.name}
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" disabled className="opacity-60" data-testid={`upgrade-soon-${s.id}`}>
                      Pack supérieur — à venir
                    </Button>
                  )}
                  {!s.bank_id && <Link to="/admin/banks/new"><Button size="sm" className="bg-zinc-900 hover:bg-zinc-700 text-white" data-testid={`create-bank-${s.id}`}>{t("subs.createBank")}</Button></Link>}
                </div>
              )}
              <Button size="sm" variant="ghost" onClick={() => setDelSubConfirm(s.id)} className="text-red-500 hover:bg-red-50 ml-auto" title="Supprimer cet abonnement">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          );
        })}
        {!subs.length && <div className="bg-white border border-zinc-200 rounded-xl p-10 text-center text-zinc-500">{t("subs.none")}</div>}
      </div>
      <InputDialog open={extraOpen} title={t("subs.extraUsersTitle")} label={`${t("subs.howMany")} (${fmtMoney(extraSub?.plan_extra_user_price || 3000, "COINS")}/${t("subs.perUser")})`} defaultValue="1" placeholder="1" onOk={doExtra} onCancel={() => setExtraOpen(false)} />
      <ConfirmDialog open={!!upgradeConfirmSub} danger={false} title={t("subs.upgradeTitle")} message={upgradeConfirmSub ? `${t("subs.upgradeMsg")} ${upgradeConfirmSub.np.name} (${fmtMoney(upgradeConfirmSub.np.price, "COINS")}) ? Le montant total du forfait sera débité de votre solde.` : ""} onOk={doUpgrade} onCancel={() => setUpgradeConfirmSub(null)} />
      <ConfirmDialog open={!!delSubConfirm} danger title="Supprimer l'abonnement" message="Supprimer cet abonnement définitivement ? Cette action est irréversible." onOk={() => deleteSub(delSubConfirm)} onCancel={() => setDelSubConfirm(null)} />
    </div>
  );
}

function MyDomains({ reload }) {
  const { t } = useAdminLang();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [orders, setOrders] = useState([]);
  const [banks, setBanks] = useState([]);
  const [delOrderConfirm, setDelOrderConfirm] = useState(null);

  const loadOrders = () => api.get("/domains/orders").then((r) => setOrders(r.data || []));

  useEffect(() => {
    loadOrders();
    api.get("/banks").then((r) => setBanks(r.data || []));
  }, []);

  const search = async (e) => {
    e?.preventDefault?.();
    if (!query.trim()) return;
    try { const { data } = await api.get("/domains/search", { params: { q: query } }); setResults(data); }
    catch (err) { toast.error(formatError(err)); }
  };

  const buy = async (d) => {
    const bankId = banks[0]?.id || null;
    try { await api.post("/domains/orders", { domain: d, type: "purchase", bank_id: bankId }); toast.success(t("domains.purchased")); loadOrders(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const deleteOrder = async (id) => {
    try { await api.delete(`/namecheap/domains/${id}`); toast.success("Commande supprimée"); setDelOrderConfirm(null); loadOrders(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="admin-domains">
      <h1 className="font-display text-3xl font-extrabold tracking-tighter mb-6">{t("domains.title")}</h1>

      <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-6">
        <h2 className="font-display text-lg font-bold mb-3">{t("domains.searchBuy")}</h2>
        <form onSubmit={search} className="flex gap-2 mb-4">
          <Input placeholder="ex: mafinance" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="dom-query" />
          <Button type="submit" className="bg-zinc-900 hover:bg-zinc-700 text-white" data-testid="dom-search">{t("domains.search")}</Button>
        </form>
        <div className="grid md:grid-cols-2 gap-2">
          {results.map((r) => (
            <div key={r.domain} className="flex items-center justify-between p-3 rounded-lg border border-zinc-200" data-testid={`dom-result-${r.domain}`}>
              <div>
                <div className="font-mono-num">{r.domain}</div>
                <div className="text-xs text-zinc-500">{r.available ? t("domains.available") : t("domains.unavailable")} · {fmtMoney(r.price, "COINS")}</div>
              </div>
              {r.available && <Button size="sm" onClick={() => buy(r.domain)} className="bg-zinc-900 hover:bg-zinc-700 text-white" data-testid={`dom-buy-${r.domain}`}>{t("domains.buy")}</Button>}
            </div>
          ))}
        </div>
      </div>

      <h2 className="font-display text-xl font-bold mb-3">{t("domains.orders")}</h2>
      <div className="space-y-2">
        {orders.map((o) => (
          <div key={o.id} className="bg-white border border-zinc-200 rounded-xl p-4 flex items-center gap-3" data-testid={`my-order-${o.id}`}>
            <div className="flex-1">
              <div className="font-mono-num font-bold">{o.domain}</div>
              <div className="text-xs text-zinc-500">{o.type === "connect" ? t("domains.dns") : t("domains.purchase")} · {fmtMoney(o.price, "COINS")}</div>
            </div>
            <Badge className={
              o.status === "active" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
              o.status === "rejected" ? "bg-red-100 text-red-700 border-red-200" :
              "bg-amber-100 text-amber-700 border-amber-200"
            }>{o.status === "pending" ? t("domains.ordered") : o.status}</Badge>
            <Button size="sm" variant="ghost" onClick={() => setDelOrderConfirm(o.id)} className="text-red-500 hover:bg-red-50 h-7 w-7 p-0" title="Supprimer cette commande">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
        {!orders.length && <div className="text-zinc-400 text-sm">{t("domains.none")}</div>}
      </div>
      <ConfirmDialog open={!!delOrderConfirm} danger title="Supprimer la commande" message="Supprimer cette commande de domaine ? Cette action est irréversible." onOk={() => deleteOrder(delOrderConfirm)} onCancel={() => setDelOrderConfirm(null)} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Referral program
// -----------------------------------------------------------------------------
const WITHDRAW_METHOD_COLORS = {
  mobile_money: "bg-blue-50 text-blue-700 border-blue-200",
  wave: "bg-cyan-50 text-cyan-700 border-cyan-200",
  crypto_usdt: "bg-yellow-50 text-yellow-700 border-yellow-200",
  transfer_to_balance: "bg-purple-50 text-purple-700 border-purple-200",
};
const WITHDRAW_STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  transferred: "bg-purple-100 text-purple-700",
};

function Referral() {
  const [data, setData] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [copied, setCopied] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [wForm, setWForm] = useState({ method: "mobile_money", phone: "", network: "bsc", address: "", amount: "" });
  const [wBusy, setWBusy] = useState(false);
  const [transferBusy, setTransferBusy] = useState(false);
  const [delConfirm, setDelConfirm] = useState(null);
  const { t } = useAdminLang();
  const fmtCoins = (n) => new Intl.NumberFormat("fr-FR").format(n ?? 0);

  const load = async () => {
    try {
      const [refRes, wRes] = await Promise.all([
        api.get("/admin/referrals"),
        api.get("/admin/referrals/withdrawals"),
      ]);
      setData(refRes.data);
      setWithdrawals(wRes.data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
  const refUrl = data ? `${FRONTEND_URL}/register?ref=${data.referral_code}` : "";

  const copy = () => {
    navigator.clipboard.writeText(refUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const submitWithdraw = async (e) => {
    e.preventDefault();
    setWBusy(true);
    try {
      await api.post("/admin/referrals/withdraw", {
        amount: parseFloat(wForm.amount),
        method: wForm.method,
        phone: wForm.method !== "crypto_usdt" ? wForm.phone : undefined,
        network: wForm.method === "crypto_usdt" ? wForm.network : undefined,
        address: wForm.method === "crypto_usdt" ? wForm.address : undefined,
      });
      toast.success("Demande de retrait envoyée !");
      setShowWithdraw(false);
      setWForm({ method: "mobile_money", phone: "", network: "bsc", address: "", amount: "" });
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setWBusy(false); }
  };

  const transferToBalance = async () => {
    if (!window.confirm(t("referral.transferConfirm"))) return;
    setTransferBusy(true);
    try {
      const { data: res } = await api.post("/admin/referrals/transfer-to-balance");
      toast.success(`${t("referral.transferred")} +${fmtCoins(res.amount)} Coins`);
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setTransferBusy(false); }
  };

  const deleteWithdrawal = async (id) => {
    try {
      await api.delete(`/admin/referrals/withdrawals/${id}`);
      setWithdrawals((p) => p.filter((w) => w.id !== id));
      setDelConfirm(null);
    } catch (err) { toast.error(formatError(err)); }
  };

  if (!data) return <div className="p-8 text-center text-zinc-400">{t("support.loading")}</div>;

  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-referral">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter flex items-center gap-2">
          <Gift className="w-7 h-7 text-[#D4AF37]" /> {t("referral.title")}
        </h1>
        <p className="text-zinc-500 mt-1">{t("referral.subtitle")}</p>
      </div>

      {/* Referral link */}
      <div className="bg-zinc-900 text-white rounded-2xl p-6">
        <div className="text-xs tracking-widest uppercase opacity-60 mb-2">{t("referral.yourLink")}</div>
        <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-3 mb-4">
          <span className="flex-1 font-mono text-sm break-all">{refUrl}</span>
          <button onClick={copy} className="shrink-0 p-1.5 rounded-lg hover:bg-white/10 transition" title={t("referral.copy")}>
            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
        <div className="grid grid-cols-4 gap-3 text-center">
          <div className="bg-white/10 rounded-xl p-3">
            <div className="text-xl font-extrabold font-mono-num">{data.total_referrals}</div>
            <div className="text-xs opacity-60 mt-0.5">{t("referral.referees")}</div>
          </div>
          <div className="bg-white/10 rounded-xl p-3">
            <div className="text-xl font-extrabold font-mono-num text-[#D4AF37]">{fmtCoins(data.validated)}</div>
            <div className="text-xs opacity-60 mt-0.5">{t("referral.validated")}</div>
          </div>
          <div className="bg-white/10 rounded-xl p-3">
            <div className="text-xl font-extrabold font-mono-num text-amber-400">{fmtCoins(data.pending)}</div>
            <div className="text-xs opacity-60 mt-0.5">{t("referral.pending")}</div>
          </div>
          <div className="bg-emerald-900/60 rounded-xl p-3 ring-1 ring-emerald-500/30">
            <div className="text-xl font-extrabold font-mono-num text-emerald-400">{fmtCoins(data.available)}</div>
            <div className="text-xs opacity-70 mt-0.5">{t("referral.available")}</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => setShowWithdraw(true)}
          disabled={(data.available ?? 0) <= 0}
          className="bg-zinc-900 text-white gap-2"
        >
          <ArrowDownToLine className="w-4 h-4" /> {t("referral.withdraw")}
        </Button>
        <Button
          variant="outline"
          onClick={transferToBalance}
          disabled={transferBusy || (data.available ?? 0) <= 0}
          className="gap-2"
        >
          <Repeat className="w-4 h-4" /> {transferBusy ? t("support.loading") : t("referral.transferBalance")}
        </Button>
      </div>

      {/* Withdraw modal */}
      <Dialog open={showWithdraw} onOpenChange={(v) => { if (!v) setShowWithdraw(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("referral.withdraw")}</DialogTitle></DialogHeader>
          <form onSubmit={submitWithdraw} className="space-y-4 py-1">
            <div>
              <Label>{t("referral.method")}</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[["mobile_money", "Mobile Money"], ["wave", "Wave"], ["crypto_usdt", "USDT Crypto"]].map(([k, lbl]) => (
                  <button key={k} type="button"
                    className={`text-xs py-2 px-1 rounded-xl border font-semibold transition ${wForm.method === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}
                    onClick={() => setWForm((f) => ({ ...f, method: k }))}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            {wForm.method !== "crypto_usdt" ? (
              <div>
                <Label>{t("referral.phone")}</Label>
                <Input required value={wForm.phone} onChange={(e) => setWForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+225 07 00 00 00 00" />
              </div>
            ) : (
              <>
                <div>
                  <Label>{t("referral.network")}</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {["bsc", "bep20", "bnb"].map((n) => (
                      <button key={n} type="button"
                        className={`text-xs py-2 rounded-xl border font-semibold uppercase transition ${wForm.network === n ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}
                        onClick={() => setWForm((f) => ({ ...f, network: n }))}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>{t("referral.address")}</Label>
                  <Input required value={wForm.address} onChange={(e) => setWForm((f) => ({ ...f, address: e.target.value }))} placeholder="0x..." className="font-mono text-xs" />
                </div>
              </>
            )}
            <div>
              <Label>{t("referral.amount")}</Label>
              <Input required type="number" min={1} max={data.available ?? 0} step="0.01"
                value={wForm.amount} onChange={(e) => setWForm((f) => ({ ...f, amount: e.target.value }))}
                placeholder={`Max: ${fmtCoins(data.available)}`} />
              <p className="text-xs text-zinc-400 mt-1">{t("referral.available")} : <strong>{fmtCoins(data.available)} Coins</strong></p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowWithdraw(false)}>Annuler</Button>
              <Button type="submit" className="bg-zinc-900 text-white" disabled={wBusy}>{wBusy ? t("support.loading") : "Envoyer"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Referees */}
      {data.referees?.length > 0 && (
        <div>
          <h2 className="font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> {t("referral.refereesTitle")} ({data.referees.length})</h2>
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
                <tr><th className="text-left p-3">{t("referral.name")}</th><th className="text-left p-3">{t("referral.email")}</th><th className="text-right p-3">{t("referral.signedUp")}</th></tr>
              </thead>
              <tbody>
                {data.referees.map((r) => (
                  <tr key={r.id} className="border-t border-zinc-100">
                    <td className="p-3 font-semibold">{r.name}</td>
                    <td className="p-3 text-zinc-500">{r.email}</td>
                    <td className="p-3 text-right text-zinc-400 text-xs">{new Date(r.created_at).toLocaleDateString("fr-FR")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Commission history */}
      <div>
        <h2 className="font-semibold mb-3 flex items-center gap-2"><Gift className="w-4 h-4 text-[#D4AF37]" /> {t("referral.commissions")}</h2>
        {!data.commissions?.length ? (
          <div className="text-zinc-400 text-sm text-center py-6 bg-white border border-zinc-200 rounded-xl">{t("referral.noCommissions")}</div>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="text-left p-3">{t("referral.referee")}</th>
                  <th className="text-left p-3">{t("referral.planBought")}</th>
                  <th className="text-right p-3">{t("referral.commission")}</th>
                  <th className="text-center p-3">{t("referral.status")}</th>
                  <th className="text-right p-3">{t("referral.date")}</th>
                </tr>
              </thead>
              <tbody>
                {(data.commissions || []).map((c) => (
                  <tr key={c.id} className="border-t border-zinc-100">
                    <td className="p-3">
                      <div className="font-semibold">{c.referee_name}</div>
                      <div className="text-xs text-zinc-400">{c.referee_email}</div>
                    </td>
                    <td className="p-3 text-zinc-600">{c.plan_name} — {fmtCoins(c.plan_price)} Coins</td>
                    <td className="p-3 text-right font-mono-num font-bold text-emerald-600">+{fmtCoins(c.commission)} Coins</td>
                    <td className="p-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${c.status === "validated" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        {c.status === "validated" ? t("referral.statusValidated") : t("referral.statusPending")}
                      </span>
                    </td>
                    <td className="p-3 text-right text-zinc-400 text-xs">{new Date(c.created_at).toLocaleDateString("fr-FR")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Withdrawal history */}
      <div>
        <h2 className="font-semibold mb-3 flex items-center gap-2"><ArrowDownToLine className="w-4 h-4" /> {t("referral.withdrawHistory")}</h2>
        {!withdrawals.length ? (
          <div className="text-zinc-400 text-sm text-center py-6 bg-white border border-zinc-200 rounded-xl">{t("referral.noWithdrawals")}</div>
        ) : (
          <div className="flex flex-col gap-3">
            {withdrawals.map((w) => (
              <div key={w.id} className="bg-white border border-zinc-200 rounded-xl p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${WITHDRAW_METHOD_COLORS[w.method] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>
                      {t(`referral.method.${w.method}`) || w.method}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${WITHDRAW_STATUS_COLORS[w.status] || "bg-zinc-100 text-zinc-600"}`}>
                      {t(`referral.status${w.status.charAt(0).toUpperCase() + w.status.slice(1)}`) || w.status}
                    </span>
                    <span className="font-bold text-sm text-zinc-900 font-mono-num">{fmtCoins(w.amount)} Coins</span>
                  </div>
                  <div className="text-xs text-zinc-500 truncate">
                    {w.method === "crypto_usdt" ? `${w.network?.toUpperCase()} · ${w.address}` : w.phone || "—"}
                  </div>
                  <div className="text-xs text-zinc-400 mt-0.5">{new Date(w.created_at).toLocaleString("fr-FR")}</div>
                </div>
                <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-red-500 shrink-0" onClick={() => setDelConfirm(w)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <Dialog open={!!delConfirm} onOpenChange={(v) => { if (!v) setDelConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Supprimer cette entrée ?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500">Cette action ne peut pas être annulée. La demande sera retirée de l'historique.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelConfirm(null)}>Annuler</Button>
            <Button variant="destructive" onClick={() => deleteWithdrawal(delConfirm?.id)}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Contact Support ──────────────────────────────────────────────────────────
function ContactSupport() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const msgsEndRef = useRef(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);
  const { t } = useAdminLang();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const load = async () => {
    try {
      const { data } = await api.get("/admin/support/messages");
      setMessages(data.messages || []);
      msgsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    } catch (e) {}
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 5000);
    return () => clearInterval(pollRef.current);
  }, []);

  const send = async () => {
    if (!input.trim()) return;
    setSending(true);
    try {
      await api.post("/admin/support/contact", { content: input });
      setInput("");
      await load();
      toast.success(t("support.sent"));
    } catch (e) { toast.error(formatError(e)); }
    finally { setSending(false); }
  };

  const sendFile = async (file) => {
    if (!file) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/support/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      toast.success(t("support.sent"));
    } catch (e) { toast.error(formatError(e)); }
    finally { setSending(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const fmtTime = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

  const renderAttachment = (att, fromSupport) => {
    if (!att) return null;
    const isImage = att.mime?.startsWith("image/");
    const url = att.url?.startsWith("http") ? att.url : `${BACKEND_URL}${att.url}`;
    if (isImage) return <img src={url} alt={att.filename} className="max-w-[220px] rounded-lg mt-1 block" />;
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
        className={`flex items-center gap-1.5 mt-1 text-xs underline ${fromSupport ? "text-zinc-600" : "text-zinc-300"}`}>
        <Paperclip className="w-3.5 h-3.5" />{att.filename}
      </a>
    );
  };

  return (
    <div data-testid="admin-support" className="max-w-3xl">
      <div className="mb-4 md:mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter flex items-center gap-2">
          <LifeBuoy className="w-6 h-6 md:w-7 md:h-7 text-zinc-700 shrink-0" />
          <span className="truncate">{t("support.title")}</span>
        </h1>
        <p className="text-zinc-500 text-sm md:text-base mt-1">{t("support.subtitle")}</p>
      </div>

      <div
        className="bg-white border border-zinc-200 rounded-xl overflow-hidden flex flex-col"
        style={{ height: "min(560px, calc(100dvh - 12rem))" }}
      >
        <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3 bg-zinc-50/50 min-h-0">
          {loading ? (
            <div className="text-center text-zinc-400 text-sm py-12">{t("support.loading")}</div>
          ) : !messages.length ? (
            <div className="text-center text-zinc-400 text-sm py-12">
              <LifeBuoy className="w-10 h-10 mx-auto mb-3 opacity-30" />
              {t("support.empty")}
            </div>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "super_admin" ? "justify-start" : "justify-end"}`}>
                <div className={`max-w-[85%] md:max-w-[75%] rounded-2xl px-3.5 md:px-4 py-2.5 text-sm ${m.sender === "super_admin" ? "bg-white border border-zinc-200 text-zinc-900 rounded-tl-sm" : "bg-zinc-900 text-white rounded-tr-sm"}`}>
                  <div className="text-xs opacity-60 mb-0.5">{m.sender === "super_admin" ? t("support.supportLabel") : t("support.you")}</div>
                  {m.content && <p className="whitespace-pre-wrap break-words">{m.content}</p>}
                  {m.attachment && renderAttachment(m.attachment, m.sender === "super_admin")}
                  <div className="text-[10px] mt-1 opacity-50">{fmtTime(m.created_at)}</div>
                </div>
              </div>
            ))
          )}
          <div ref={msgsEndRef} />
        </div>
        <div
          className="p-2 md:p-3 border-t border-zinc-200 bg-white"
          style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
        >
          <div className="flex gap-1.5 md:gap-2 items-end">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("support.placeholder")}
              rows={1}
              className="flex-1 border border-zinc-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-zinc-900 resize-none min-h-[42px] max-h-32"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              disabled={sending}
            />
            <button onClick={() => fileInputRef.current?.click()} disabled={sending}
              className="border border-zinc-200 rounded-xl px-2.5 h-[42px] text-zinc-500 hover:bg-zinc-50 disabled:opacity-40 transition shrink-0" title={t("msgs.attach")}>
              <Paperclip size={18} />
            </button>
            <input ref={fileInputRef} type="file" className="hidden"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.mp3,.wav,.ogg,.txt"
              onChange={(e) => e.target.files?.[0] && sendFile(e.target.files[0])} />
            {/* Send button — icon-only on mobile, text on desktop */}
            <Button
              onClick={send}
              disabled={!input.trim() || sending}
              className="bg-zinc-900 hover:bg-zinc-700 text-white h-[42px] px-3 md:px-4 shrink-0"
              title={t("support.send")}
            >
              <SendIcon className="w-4 h-4 md:hidden" />
              <span className="hidden md:inline">{sending ? t("support.sending") : t("support.send")}</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Admin Wallet
// -----------------------------------------------------------------------------
const WALLET_PRESETS = [1000, 2000, 5000, 10000, 25000, 50000];

function AdminWallet() {
  const { t } = useAdminLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState([]);
  const [topupOpen, setTopupOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [userCurrency, setUserCurrency] = useState(null); // { currency, rate, name }

  // Detect user's currency from geo + fetch live rate
  useEffect(() => {
    (async () => {
      try {
        const geo = await api.get("/public/geo");
        const curr = geo.data?.currency;
        if (!curr || curr === "XOF" || curr === "XAF") return;
        const r = await api.get(`/public/coin-rate?currency=${curr}`);
        if (r.data?.rate) {
          setUserCurrency({ currency: curr, rate: r.data.rate });
        }
      } catch {}
    })();
  }, []);

  // Format conversion line: "1 Coin = 1 FCFA = 0.0018 USD = 2.43 NGN"
  const conversionLine = (() => {
    const parts = ["1 Coin = 1 FCFA"];
    if (userCurrency?.rate) {
      const v = userCurrency.rate;
      const formatted = v < 0.01 ? v.toFixed(5) : v < 1 ? v.toFixed(4) : v.toFixed(2);
      parts.push(`${formatted} ${userCurrency.currency}`);
    }
    return parts.join(" ≈ ");
  })();

  const load = () => api.get("/admin/wallet").then((r) => {
    setBalance(r.data.balance);
    setHistory(r.data.history || []);
  }).catch(() => {});

  // Reset loading when browser restores page from bfcache (user pressed Back)
  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) setLoading(false);
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  // Handle PayCore return: ?topup_pid=xxx or ?topup_cancel=1
  useEffect(() => {
    const pid = searchParams.get("topup_pid");
    const cancelled = searchParams.get("topup_cancel");
    if (pid) {
      setSearchParams({}, { replace: true });
      setChecking(true);
      const poll = async () => {
        for (let i = 0; i < 10; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          try {
            const { data } = await api.get(`/admin/wallet/topup/status/${pid}`);
            if (data.status === "credited") {
              await load();
              toast.success("Recharge confirmée ! Votre solde a été crédité.");
              setChecking(false);
              return;
            }
          } catch (_) {}
        }
        await load();
        toast.info("Paiement en cours de traitement — le solde sera mis à jour dans quelques instants.");
        setChecking(false);
      };
      poll();
    } else if (cancelled) {
      setSearchParams({}, { replace: true });
      setLoading(false);
      toast.error("Paiement annulé.");
    }
  }, []);

  useEffect(() => { load(); }, []);

  const initiateTopup = async () => {
    const n = parseInt(amount, 10);
    if (!n || n < 100) { toast.error("Montant minimum : 100 FCFA"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/wallet/topup/initiate", { amount: n });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatError(err));
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-wallet">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-extrabold tracking-tighter">{t("wallet.title")}</h1>
        <p className="text-zinc-500 mt-1">{t("wallet.subtitle")}</p>
      </div>

      {checking && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700 font-medium flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          Vérification du paiement en cours…
        </div>
      )}

      {/* Balance card */}
      <div className="rounded-2xl p-6 mb-6 flex items-center justify-between text-white"
        style={{ background: "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)", boxShadow: "0 16px 40px -16px rgba(59,130,246,.6)" }}>
        <div>
          <div className="text-xs tracking-widest uppercase opacity-70 mb-1">{t("wallet.availableBalance")}</div>
          <div className="text-4xl font-extrabold tracking-tight font-mono-num" data-testid="wallet-balance"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {balance === null ? "…" : fmtMoney(balance, "COINS")}
          </div>
          <div className="text-xs opacity-60 mt-1">{conversionLine}</div>
        </div>
        <Button onClick={() => setTopupOpen(true)} data-testid="wallet-topup-btn"
          className="font-bold rounded-full px-5"
          style={{ background: "#fff", color: "#3B82F6", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,.15)" }}>
          <WalletIcon className="w-4 h-4 mr-1.5" /> {t("wallet.topup")}
        </Button>
      </div>

      {/* History */}
      <h2 className="font-semibold text-base mb-3">{t("wallet.history")}</h2>
      <div className="space-y-2">
        {history.length === 0 && <div className="text-zinc-400 text-sm py-4 text-center">{t("wallet.none")}</div>}
        {history.map((tx) => (
          <div key={tx.id} className="bg-white border border-zinc-200 rounded-xl p-4 flex items-center justify-between" data-testid={`wallet-tx-${tx.id}`}>
            <div>
              <div className="font-medium text-sm">{tx.label}</div>
              <div className="text-xs text-zinc-400 mt-0.5">{new Date(tx.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
            </div>
            <span className={`font-bold font-mono-num text-sm ${tx.type === "credit" ? "text-green-600" : "text-red-600"}`}>
              {tx.type === "credit" ? "+" : "−"}{fmtMoney(tx.amount, "COINS")}
            </span>
          </div>
        ))}
      </div>

      {/* Top-up dialog — PayCore */}
      <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
        <DialogContent className="max-w-sm overflow-hidden p-0">
          {/* Header with gradient */}
          <div className="p-5 text-white" style={{ background: "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)" }}>
            <div className="flex items-center gap-2">
              <WalletIcon className="w-5 h-5 opacity-80" />
              <DialogTitle className="text-white text-lg font-bold m-0" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Recharger mon portefeuille
              </DialogTitle>
            </div>
          </div>

          <div className="p-5 space-y-4">
            {/* Custom amount — top, visible */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block" style={{ color: "#4a4a6a" }}>
                Montant à recharger
              </label>
              <div className="flex items-center gap-2 rounded-xl border-2 px-3 py-2.5 transition-colors focus-within:border-blue-500"
                style={{ borderColor: "#e2e8f8" }}>
                <Input
                  type="number"
                  min={100}
                  step={100}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Ex: 5000"
                  className="flex-1 border-0 p-0 text-lg font-bold focus-visible:ring-0 focus-visible:ring-offset-0"
                  style={{ color: "#0d0d1a" }}
                />
                <span className="text-sm font-semibold shrink-0" style={{ color: "#3B82F6" }}>Coins</span>
              </div>
              {amount && userCurrency?.rate && parseInt(amount, 10) > 0 && (
                <div className="text-xs mt-1.5 ml-1" style={{ color: "#4a4a6a" }}>
                  ≈ {(parseInt(amount, 10) * userCurrency.rate).toLocaleString(undefined, { maximumFractionDigits: 2 })} {userCurrency.currency}
                </div>
              )}
            </div>

            {/* Presets */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block" style={{ color: "#4a4a6a" }}>
                Montants rapides
              </label>
              <div className="grid grid-cols-3 gap-2">
                {WALLET_PRESETS.map((p) => {
                  const sel = amount === String(p);
                  return (
                    <button
                      key={p}
                      onClick={() => setAmount(String(p))}
                      className="rounded-xl py-2.5 text-sm font-bold transition-all hover:-translate-y-0.5"
                      style={sel
                        ? { background: "linear-gradient(135deg, #3B82F6, #8B5CF6)", color: "#fff", border: "none", boxShadow: "0 6px 16px -6px rgba(59,130,246,.5)" }
                        : { background: "rgba(59,130,246,.06)", color: "#3B82F6", border: "1px solid rgba(59,130,246,.2)" }}
                    >
                      {p >= 1000 ? `${p / 1000}K` : p}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="text-xs text-center px-2" style={{ color: "#4a4a6a" }}>{conversionLine} · Min. 100 Coins</div>

            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => setTopupOpen(false)} className="flex-1 rounded-full">Annuler</Button>
              <button
                onClick={initiateTopup}
                disabled={loading || !amount || parseInt(amount, 10) < 100}
                className="flex-1 rounded-full py-2.5 text-sm font-bold text-white transition-all disabled:opacity-40 hover:-translate-y-0.5"
                style={{ background: "linear-gradient(135deg, #3B82F6, #8B5CF6)", boxShadow: "0 8px 20px -8px rgba(59,130,246,.5)" }}
              >
                {loading ? "Redirection…" : `Payer ${amount && parseInt(amount,10) >= 100 ? `${parseInt(amount,10).toLocaleString()} Coins` : ""} →`}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
